import React, { useCallback, useEffect, useMemo, useRef, useState, createContext, useContext } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import GestionePresenze from "./modules/Presenze";
import GestioneConsulente from "./modules/Consulente";
import { useAppVersion } from "./hooks/useAppVersion";
import FlameHeart from "./components/FlameHeart";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LS_KEY = "accdb_to_csv.session.v1";
const AUTH_TOKEN_KEY = "fuocoinhub.auth.token.v1";
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

// Attach Bearer token to every outgoing request whenever the user is logged in.
axios.interceptors.request.use((cfg) => {
  const t = localStorage.getItem(AUTH_TOKEN_KEY);
  if (t) cfg.headers = { ...(cfg.headers || {}), Authorization: `Bearer ${t}` };
  return cfg;
});

const formatBytes = (bytes) => {
  if (bytes == null) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let v = bytes;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
};

const formatDate = (epochSeconds) => {
  if (!epochSeconds) return "—";
  const d = new Date(epochSeconds * 1000);
  if (Number.isNaN(d.getTime())) return "—";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const formatItalianNumber = (v, decimals = 2) => {
  if (v == null || v === "") return "";
  const n = typeof v === "number" ? v : Number(String(v).replace(",", "."));
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString("it-IT", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

// Convert "YYYY-MM-DD HH:MM:SS" (or just "YYYY-MM-DD") to "DD/MM/YYYY".
// Returns original value if it doesn't look like a date.
const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})(?:[T ]\d{2}:\d{2}(?::\d{2})?)?$/;
const formatItalianDate = (v) => {
  if (v == null || v === "") return "";
  const s = String(v).trim();
  const m = ISO_DATE_RE.exec(s);
  if (!m) return s;
  return `${m[3]}/${m[2]}/${m[1]}`;
};

// Hide low-level archives from the sidebar; the user works on Documenti + lookups.
const HIDDEN_SIDEBAR_TABLES = new Set(["Bolle", "BolleTipi", "BolleTotali"]);
// Reserved view identifiers for configuration pages
const SETUP_VIEW_KEY = "__setup__";
const AUTOMEZZI_VIEW_KEY = "__automezzi__";
const SOGGETTI_VIEW_KEY = "__soggetti__";
const DATI_AZIENDA_VIEW_KEY = "__dati_azienda__";
const RIMBORSI_VIEW_KEY = "__rimborsi__";
const DRIVE_VIEW_KEY = "__drive__";

// Per-table visible-column whitelist. Order matters — the grid renders in this order.
// Use the literal column names returned by the backend.
const COLUMN_WHITELIST = {
  Documenti: [
    "Tipo",
    "Data",
    "Numero",
    "Anagrafica",
    "Totale",
    "Pagamento",
  ],
  Ditte: [
    "ID ditta",
    "Ragione sociale 1",
    "Indirizzo 1",
    "CAP",
    "City",
    "Provincia",
    "Codice Fiscale",
    "VatNumber",
  ],
  Pagamenti: [
    "ID pagamento",
    "Descrizione pagamento",
  ],
};
// Friendlier header labels for whitelisted columns
const COLUMN_LABELS = {
  "Ragione sociale 1": "Ragione sociale",
  "Indirizzo 1": "Indirizzo",
  "City": "Città",
  "Provincia": "Prov.",
  "Codice Fiscale": "CF",
  "VatNumber": "Partita IVA",
  "Descrizione pagamento": "Descrizione",
};

const Icon = {
  Download: (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" {...props}>
      <path d="M12 4v12M12 16l-5-5M12 16l5-5" strokeLinecap="square" />
      <path d="M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3" strokeLinecap="square" />
    </svg>
  ),
  Database: (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" {...props}>
      <ellipse cx="12" cy="5" rx="8" ry="3" />
      <path d="M4 5v6c0 1.66 3.58 3 8 3s8-1.34 8-3V5" />
      <path d="M4 11v6c0 1.66 3.58 3 8 3s8-1.34 8-3v-6" />
    </svg>
  ),
  Refresh: (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" {...props}>
      <path d="M3 12a9 9 0 1 0 3-6.7" strokeLinecap="square" />
      <path d="M3 4v5h5" strokeLinecap="square" />
    </svg>
  ),
  X: (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" {...props}>
      <path d="M6 6l12 12M18 6L6 18" strokeLinecap="square" />
    </svg>
  ),
  Settings: (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" {...props}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.9 2.9l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.9-2.9l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 4.6 9a1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.9-2.9l.1.1a1.7 1.7 0 0 0 1.8.3H9A1.7 1.7 0 0 0 10 3.1V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.9 2.9l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
    </svg>
  ),
  Save: (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" {...props}>
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
      <polyline points="17 21 17 13 7 13 7 21" />
      <polyline points="7 3 7 8 15 8" />
    </svg>
  ),
  Truck: (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" {...props}>
      <path d="M1 3h15v13H1zM16 8h4l3 3v5h-7V8z" strokeLinecap="square" />
      <circle cx="5.5" cy="18.5" r="2.5" />
      <circle cx="18.5" cy="18.5" r="2.5" />
    </svg>
  ),
  Plus: (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" {...props}>
      <path d="M12 5v14M5 12h14" strokeLinecap="square" />
    </svg>
  ),
  Trash: (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" {...props}>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  ),
  MapPin: (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" {...props}>
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  ),
  User: (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" {...props}>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" strokeLinecap="square" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  Receipt: (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" {...props}>
      <path d="M4 2v20l3-2 3 2 3-2 3 2 3-2 1 2V2L4 2z" strokeLinejoin="miter" />
      <path d="M8 7h8M8 11h8M8 15h5" strokeLinecap="square" />
    </svg>
  ),
  Building: (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" {...props}>
      <rect x="4" y="3" width="16" height="18" />
      <path d="M8 7h2M14 7h2M8 11h2M14 11h2M8 15h2M14 15h2M10 21v-4h4v4" strokeLinecap="square" />
    </svg>
  ),
  Printer: (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" {...props}>
      <path d="M6 9V2h12v7M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" strokeLinecap="square" />
      <rect x="6" y="14" width="12" height="8" />
    </svg>
  ),
};

function GestioneViaggi({ goHome, isAdmin = false }) {
  const version = useAppVersion();
  // session = {session_id, filename, size, created_at}
  const [session, setSession] = useState(null);
  // importedTables = list from /import-preset or /imported
  const [importedTables, setImportedTables] = useState(null);
  const [activeView, setActiveView] = useState("Documenti");

  const [importing, setImporting] = useState(false);
  const [importStep, setImportStep] = useState(""); // visible status during import
  const [restoring, setRestoring] = useState(true); // initial restore from localStorage

  const fileInputRef = useRef(null);

  // ---- Persistence: try to restore session from localStorage ---------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Prefer the session id we stored locally; if missing, ask the server
        // for the currently active persisted import.
        const raw = localStorage.getItem(LS_KEY);
        let session_id = null;
        let filename = null;
        if (raw) {
          try {
            const obj = JSON.parse(raw);
            session_id = obj.session_id || null;
            filename = obj.filename || null;
          } catch {}
        }
        if (!session_id) {
          try {
            const cur = await axios.get(`${API}/access/current`);
            if (cur.data && cur.data.present && cur.data.session_id) {
              session_id = cur.data.session_id;
              filename = cur.data.filename;
              localStorage.setItem(
                LS_KEY,
                JSON.stringify({ session_id, filename }),
              );
            }
          } catch {}
        }
        if (!session_id) return;
        const r = await axios.get(`${API}/access/${session_id}/imported`);
        if (cancelled) return;
        setSession({
          session_id,
          filename: r.data.filename || filename || "(restored)",
          size: r.data.size,
          created_at: r.data.created_at,
        });
        setImportedTables(r.data.tables);
        const first = r.data.tables.find((t) => t.is_view) || r.data.tables[0];
        if (first) setActiveView(first.name);
      } catch (e) {
        // session expired or not found — clear stale storage
        localStorage.removeItem(LS_KEY);
      } finally {
        if (!cancelled) setRestoring(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ---- Import flow: upload + preset, fully automatic -----------------------
  const triggerFilePicker = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const runImport = useCallback(async (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().match(/\.(accdb|rdy)$/)) {
      toast.error("Solo file .accdb o .rdy");
      return;
    }
    setImporting(true);
    try {
      // 1) Upload — chunked to bypass ingress body-size limits in production.
      // 1 MB chunks: well under the typical nginx-ingress default of 1 MB
      // when proxy_request_buffering is off, so individual requests are never
      // rejected by the proxy. Larger chunks have empirically hung in prod.
      const CHUNK_SIZE = 1 * 1024 * 1024; // 1 MB
      const CHUNK_TIMEOUT_MS = 60 * 1000;  // 60 s per chunk
      const MAX_CHUNK_ATTEMPTS = 4;
      const totalBytes = f.size;
      const totalChunks = Math.max(1, Math.ceil(totalBytes / CHUNK_SIZE));

      setImportStep(
        totalChunks === 1
          ? "Caricamento file…"
          : `Caricamento file… 0% (0/${totalChunks})`,
      );

      const initResp = await axios.post(`${API}/access/upload/init`, {
        filename: f.name,
      });
      const uploadId = initResp.data.upload_id;

      const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, totalBytes);
        const blob = f.slice(start, end);

        let attempt = 0;
        let lastErr = null;
        while (attempt < MAX_CHUNK_ATTEMPTS) {
          attempt += 1;
          try {
            await axios.post(
              `${API}/access/upload/chunk/${uploadId}?offset=${start}`,
              blob,
              {
                headers: { "Content-Type": "application/octet-stream" },
                timeout: CHUNK_TIMEOUT_MS,
                maxContentLength: Infinity,
                maxBodyLength: Infinity,
              },
            );
            lastErr = null;
            break;
          } catch (err) {
            lastErr = err;
            // Retry on network / 5xx; abort on 4xx (client error, e.g. 413)
            const status = err?.response?.status;
            if (status && status >= 400 && status < 500) break;
            if (attempt < MAX_CHUNK_ATTEMPTS) {
              setImportStep(
                `Riprovo chunk ${i + 1}/${totalChunks} (tentativo ${attempt + 1})…`,
              );
              await sleep(500 * attempt);
            }
          }
        }
        if (lastErr) {
          const status = lastErr?.response?.status;
          const detail =
            lastErr?.response?.data?.detail ||
            lastErr?.message ||
            "errore di rete";
          throw new Error(
            `Upload interrotto al ${Math.round(((i) / totalChunks) * 100)}% ` +
              `(chunk ${i + 1}/${totalChunks}` +
              (status ? `, HTTP ${status}` : "") +
              `): ${detail}`,
          );
        }

        const pct = Math.round(((i + 1) / totalChunks) * 100);
        setImportStep(`Caricamento file… ${pct}% (${i + 1}/${totalChunks})`);
      }

      setImportStep("Finalizzazione upload…");
      const up = await axios.post(
        `${API}/access/upload/finalize/${uploadId}`,
        null,
        { timeout: 30000 },
      );
      const sid = up.data.session_id;
      const filename = up.data.filename;
      const size = f.size;
      const created_at = Date.now() / 1000;
      const jobId = up.data.job_id;

      // 2) Poll the background job until done.
      // Each /job poll is a tiny request (< 1 KB) so it never trips the
      // production proxy's request timeout, regardless of how long the
      // parsing takes for huge .accdb files.
      let result = null;
      while (true) {
        await sleep(2000);
        let job;
        try {
          const jr = await axios.get(`${API}/access/job/${jobId}`, {
            timeout: 15000,
          });
          job = jr.data;
        } catch (err) {
          // transient network/proxy hiccup on a poll request — keep trying
          continue;
        }
        const pct = Math.max(0, Math.min(100, job.progress || 0));
        const elapsed = job.elapsed_seconds
          ? ` · ${Math.round(job.elapsed_seconds)}s`
          : "";
        setImportStep(
          `${job.step || "Elaborazione archivi…"} ${pct}%${elapsed}`,
        );
        if (job.status === "done") {
          result = job.result;
          break;
        }
        if (job.status === "error") {
          throw new Error(job.error || "Import fallito");
        }
      }

      // 3) Replace any previous session and persist
      // Best-effort delete of the previous session so its temp file is purged
      const previousSid = session?.session_id;
      if (previousSid && previousSid !== sid) {
        axios.delete(`${API}/access/${previousSid}`).catch(() => {});
      }
      setSession({ session_id: sid, filename, size, created_at });
      setImportedTables(result.tables);
      const first = result.tables.find((t) => t.is_view) || result.tables[0];
      setActiveView(first ? first.name : null);
      localStorage.setItem(
        LS_KEY,
        JSON.stringify({ session_id: sid, filename })
      );
      toast.success(`Import completato: ${result.tables.length} archivi.`);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Import fallito";
      toast.error(typeof msg === "string" ? msg : "Import fallito");
    } finally {
      setImporting(false);
      setImportStep("");
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [session]);

  const onInputChange = (e) => {
    const f = e.target.files?.[0];
    runImport(f);
  };

  return (
    <div className="App">
      <div className="grain" />
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            borderRadius: 0,
            border: "1px solid #0A0A0A",
            background: "#fff",
            color: "#0A0A0A",
            fontFamily: "JetBrains Mono, monospace",
            fontSize: 13,
          },
        }}
      />

      {/* Hidden file input controlled programmatically */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".accdb,.rdy"
        className="hidden"
        onChange={onInputChange}
        data-testid="file-input"
      />

      <div className="shell">
        {/* Top bar */}
        <header className="top-bar">
          <div className="flex items-center gap-6">
            <button
              type="button"
              onClick={() => goHome && goHome()}
              className="font-mono text-[10px] uppercase tracking-widest text-white/60 hover:text-[var(--c-accent)] flex items-center gap-1.5"
              data-testid="viaggi-go-home"
            >
              ← Menu principale
            </button>
            <div className="flex items-center gap-3">
              <FlameHeart size={28} />
              <div
                className="font-display text-lg leading-none text-white"
                data-testid="app-logo"
              >
                FUOCOIN<span style={{ color: "var(--c-accent)" }}> HUB </span>Spese di Viaggio
              </div>
              <span className="font-mono text-xs text-white/50 hidden sm:inline" data-testid="app-version-header">
                v{version}
              </span>
            </div>
          </div>
        </header>

        {/* Importing overlay */}
        {importing && <ImportOverlay step={importStep} />}

        {/* Main */}
        {restoring ? (
          <RestoringState />
        ) : (
          <ViewerStage
            session={session}
            importedTables={importedTables}
            activeView={activeView}
            setActiveView={setActiveView}
            onImportClick={triggerFilePicker}
            importing={importing}
            isAdmin={isAdmin}
          />
        )}

        {/* Footer */}
        <footer
          className="border-t border-[var(--c-border-soft)] py-4 px-6 sm:px-10 font-mono text-[11px] text-[var(--c-text-muted)]"
          data-testid="app-footer"
        >
          <div>FUOCOIN HUB — gestione documenti e presenze</div>
        </footer>
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------------- */
function RestoringState() {
  return (
    <main className="flex-1 w-full flex items-center justify-center" data-testid="restoring">
      <div className="text-center">
        <div className="scanline-bar w-64 mx-auto" />
        <div className="font-mono text-xs uppercase tracking-[0.25em] text-[var(--c-text-muted)] mt-3">
          Ripristino sessione…
        </div>
      </div>
    </main>
  );
}

/* ----------------------------------------------------------------------- */
function EmptyState({ onImportClick, importing }) {
  return (
    <main className="flex-1 w-full flex items-center justify-center px-6 py-16" data-testid="empty-state">
      <div className="max-w-xl text-center">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--c-text-muted)] mb-4">
          <span style={{ color: "var(--c-accent)" }}>■</span> Nessun archivio caricato
        </div>
        <h1 className="font-display text-4xl sm:text-5xl leading-[0.95]">
          Lista <span style={{ color: "var(--c-accent)" }}>documenti</span>.
        </h1>
        <p className="mt-5 text-[var(--c-text-muted)] font-body text-base">
          Carica un file <span className="font-mono">.accdb</span> o{" "}
          <span className="font-mono">.rdy</span> e l’importazione (Bolle ≥ 2024,
          BolleTipi, BolleTotali, Ditte, Pagamenti + vista Documenti) avviene
          tutta in automatico.
        </p>
        <button
          type="button"
          onClick={onImportClick}
          disabled={importing}
          className="btn-primary mt-8 px-10 py-4 font-black uppercase tracking-widest text-base flex items-center gap-2 mx-auto disabled:opacity-50"
          data-testid="empty-import-btn"
        >
          Importa
        </button>
      </div>
    </main>
  );
}

/* ----------------------------------------------------------------------- */
function ImportOverlay({ step }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(255,255,255,0.85)", backdropFilter: "blur(4px)" }}
      data-testid="import-overlay"
    >
      <div className="border border-[var(--c-text)] bg-white px-8 py-6 max-w-md w-[90%]">
        <div className="scanline-bar" />
        <div className="font-mono text-xs uppercase tracking-[0.25em] text-[var(--c-text)] mt-4">
          {step || "Importazione in corso…"}
        </div>
        <div className="font-mono text-[10px] text-[var(--c-text-muted)] mt-2">
          Operazione automatica. Può richiedere fino a un minuto su file grandi.
        </div>
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/* Viewer stage: sidebar + paginated table grid                              */
/* ----------------------------------------------------------------------- */
function ViewerStage({ session, importedTables, activeView, setActiveView, onImportClick, importing, isAdmin = false }) {
  const PAGE_SIZE = 100;
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const tables = importedTables || [];
  const hasImport = Boolean(session && tables.length > 0);

  // Documenti-specific filters
  const [fTipo, setFTipo] = useState("");
  const [fDataFrom, setFDataFrom] = useState("");
  const [fDataTo, setFDataTo] = useState("");
  const [fPagamento, setFPagamento] = useState("");
  const [fCliente, setFCliente] = useState("");

  // Reset page + filters when changing view
  useEffect(() => {
    setPage(0);
    setData(null);
    setErr("");
    setFTipo("");
    setFDataFrom("");
    setFDataTo("");
    setFPagamento("");
    setFCliente("");
  }, [activeView]);

  const isDocumenti = activeView === "Documenti";

  // Fetch when activeView/page change OR when import completes (importedTables ref)
  useEffect(() => {
    if (!activeView || activeView === SETUP_VIEW_KEY || activeView === AUTOMEZZI_VIEW_KEY) return;
    if (!session?.session_id) return;
    let cancelled = false;
    setLoading(true);
    setErr("");
    // For Documenti view we load ALL rows once for client-side filtering;
    // for the raw tables we paginate.
    const params = isDocumenti
      ? { offset: 0, limit: 10000 }
      : { offset: page * PAGE_SIZE, limit: PAGE_SIZE };
    axios
      .get(
        `${API}/access/${session.session_id}/imported/${encodeURIComponent(activeView)}`,
        { params }
      )
      .then((r) => {
        if (cancelled) return;
        setData(r.data);
      })
      .catch((e) => {
        if (cancelled) return;
        setErr(e?.response?.data?.detail || e?.message || "Errore caricamento");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [activeView, page, session, importedTables, isDocumenti]);

  // ---- Documenti filtering (client-side) ---------------------------------
  // Documenti columns are: ["Tipo","Data","Numero","Anagrafica","Totale","Pagamento"]
  // Special grouped Tipo filter: "FATTURE" matches any of these tipi.
  const FATTURE_GROUP_KEY = "__FATTURE__";
  const FATTURE_TIPI = new Set([
    "Fattura differita",
    "Fattura accompagnatoria",
  ]);

  const documentiFiltered = useMemo(() => {
    if (!isDocumenti || !data) return null;
    const cols = data.columns;
    const iTipo = cols.indexOf("Tipo");
    const iData = cols.indexOf("Data");
    const iAna = cols.indexOf("Anagrafica");
    const iPag = cols.indexOf("Pagamento");
    const search = fCliente.trim().toLowerCase();
    const df = (fDataFrom || "").trim();
    const dt = (fDataTo || "").trim();
    return data.rows.filter((r) => {
      if (fTipo) {
        const tipo = String(r[iTipo] || "");
        if (fTipo === FATTURE_GROUP_KEY) {
          if (!FATTURE_TIPI.has(tipo)) return false;
        } else if (tipo !== fTipo) {
          return false;
        }
      }
      if (fPagamento && String(r[iPag] || "") !== fPagamento) return false;
      if (search && !String(r[iAna] || "").toLowerCase().includes(search))
        return false;
      if (df || dt) {
        // Data values are strings like "2024-01-02 00:00:00" — ISO prefix compare works.
        const d = String(r[iData] || "").slice(0, 10);
        if (df && d < df) return false;
        if (dt && d > dt) return false;
      }
      return true;
    });
  }, [isDocumenti, data, fTipo, fDataFrom, fDataTo, fPagamento, fCliente]);

  // Distinct values for Tipo and Pagamento dropdowns
  const documentiOptions = useMemo(() => {
    if (!isDocumenti || !data) return { tipi: [], pagamenti: [] };
    const iTipo = data.columns.indexOf("Tipo");
    const iPag = data.columns.indexOf("Pagamento");
    const tipi = new Set();
    const pagamenti = new Set();
    for (const r of data.rows) {
      if (r[iTipo]) tipi.add(String(r[iTipo]));
      if (r[iPag]) pagamenti.add(String(r[iPag]));
    }
    return {
      tipi: Array.from(tipi).sort(),
      pagamenti: Array.from(pagamenti).sort(),
    };
  }, [isDocumenti, data]);

  // Pagination over filtered set for Documenti
  const documentiPage = useMemo(() => {
    if (!isDocumenti || !documentiFiltered) return null;
    const start = page * PAGE_SIZE;
    return documentiFiltered.slice(start, start + PAGE_SIZE);
  }, [isDocumenti, documentiFiltered, page]);

  // Recompute page bound if filters reduce row count
  useEffect(() => {
    if (!isDocumenti || !documentiFiltered) return;
    const max = Math.max(0, Math.ceil(documentiFiltered.length / PAGE_SIZE) - 1);
    if (page > max) setPage(max);
  }, [isDocumenti, documentiFiltered, page]);

  const downloadCsv = () => {
    if (!activeView) return;
    const url = `${API}/access/${session.session_id}/imported/${encodeURIComponent(activeView)}/csv`;
    const a = document.createElement("a");
    a.href = url;
    a.download = `${activeView}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const totalPages = data
    ? isDocumenti
      ? Math.max(1, Math.ceil((documentiFiltered?.length || 0) / PAGE_SIZE))
      : Math.max(1, Math.ceil(data.total_rows / PAGE_SIZE))
    : 1;
  const activeMeta = tables.find((t) => t.name === activeView);
  const visibleRowCount = isDocumenti
    ? documentiFiltered?.length || 0
    : data?.total_rows || 0;

  // Apply per-table column whitelist if defined.
  const { visibleColumns, visibleColumnIndexes } = useMemo(() => {
    if (!data) return { visibleColumns: [], visibleColumnIndexes: [] };
    const whitelist = COLUMN_WHITELIST[activeView];
    if (!whitelist) {
      return {
        visibleColumns: data.columns,
        visibleColumnIndexes: data.columns.map((_, i) => i),
      };
    }
    const cols = [];
    const idxs = [];
    for (const name of whitelist) {
      const i = data.columns.indexOf(name);
      if (i >= 0) {
        cols.push(name);
        idxs.push(i);
      }
    }
    return { visibleColumns: cols, visibleColumnIndexes: idxs };
  }, [data, activeView]);

  return (
    <main className="flex-1 w-full flex overflow-hidden" data-testid="viewer-stage">
      {/* Sidebar */}
      <aside className="w-64 border-r border-[var(--c-text)] bg-[var(--c-text)] text-white flex flex-col">
        <div className="p-5 border-b border-white/10">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] opacity-60">
            Archivio
          </div>
          <div
            className="font-mono text-sm truncate mt-1"
            title={session?.filename || ""}
            data-testid="viewer-filename"
          >
            {session?.filename || <span className="opacity-50">(nessuno)</span>}
          </div>
          <div className="font-mono text-[10px] opacity-60 mt-2 leading-relaxed" data-testid="viewer-file-meta">
            {session ? (
              <>
                {formatDate(session.created_at)}
                {session.size != null && (
                  <> · {formatBytes(session.size)}</>
                )}
              </>
            ) : (
              "Nessun archivio caricato"
            )}
          </div>
        </div>
        <nav className="flex-1 py-3 overflow-y-auto thin-scroll" data-testid="viewer-nav">
          {(hasImport
            ? tables.filter((t) => !HIDDEN_SIDEBAR_TABLES.has(t.name))
            : [
                { name: "Ditte", row_count: 0 },
                { name: "Pagamenti", row_count: 0 },
                { name: "Documenti", row_count: 0 },
              ]
          ).map((t) => (
            <button
              key={t.name}
              type="button"
              onClick={() => setActiveView(t.name)}
              disabled={!hasImport}
              className={`w-full text-left px-5 py-3 font-mono text-sm transition-colors flex items-center justify-between gap-2 disabled:opacity-40 disabled:cursor-default ${
                activeView === t.name
                  ? "bg-[var(--c-accent)] text-white"
                  : "hover:bg-white/10 disabled:hover:bg-transparent"
              }`}
              data-testid={`nav-${t.name}`}
            >
              <span className="truncate flex items-center gap-2">
                {t.name}
              </span>
              <span className="text-[10px] opacity-70 whitespace-nowrap">
                {t.row_count.toLocaleString()}
              </span>
            </button>
          ))}
          <div className="mx-5 my-3 border-t border-white/10" />
          <div className="px-5 py-2 font-mono text-[9px] uppercase tracking-[0.25em] text-white/40">
            Rimborsi
          </div>
          <button
            type="button"
            onClick={() => setActiveView(RIMBORSI_VIEW_KEY)}
            className={`w-full text-left px-5 py-3 font-mono text-sm transition-colors flex items-center justify-between gap-2 ${
              activeView === RIMBORSI_VIEW_KEY
                ? "bg-[var(--c-accent)] text-white"
                : "hover:bg-white/10"
            }`}
            data-testid="nav-rimborsi"
          >
            <span className="truncate flex items-center gap-2">
              <Icon.Receipt className="w-4 h-4" />
              Schede Rimborsi
            </span>
          </button>
          <div className="mx-5 my-3 border-t border-white/10" />
          <div className="px-5 py-2 font-mono text-[9px] uppercase tracking-[0.25em] text-white/40">
            Configurazione
          </div>
          <button
            type="button"
            onClick={() => setActiveView(SETUP_VIEW_KEY)}
            className={`w-full text-left px-5 py-3 font-mono text-sm transition-colors flex items-center justify-between gap-2 ${
              activeView === SETUP_VIEW_KEY
                ? "bg-[var(--c-accent)] text-white"
                : "hover:bg-white/10"
            }`}
            data-testid="nav-setup"
          >
            <span className="truncate flex items-center gap-2">
              <Icon.Settings className="w-4 h-4" />
              Sedi
            </span>
          </button>
          <button
            type="button"
            onClick={() => setActiveView(AUTOMEZZI_VIEW_KEY)}
            className={`w-full text-left px-5 py-3 font-mono text-sm transition-colors flex items-center justify-between gap-2 ${
              activeView === AUTOMEZZI_VIEW_KEY
                ? "bg-[var(--c-accent)] text-white"
                : "hover:bg-white/10"
            }`}
            data-testid="nav-automezzi"
          >
            <span className="truncate flex items-center gap-2">
              <Icon.Truck className="w-4 h-4" />
              Automezzi
            </span>
          </button>
          <button
            type="button"
            onClick={() => setActiveView(SOGGETTI_VIEW_KEY)}
            className={`w-full text-left px-5 py-3 font-mono text-sm transition-colors flex items-center justify-between gap-2 ${
              activeView === SOGGETTI_VIEW_KEY
                ? "bg-[var(--c-accent)] text-white"
                : "hover:bg-white/10"
            }`}
            data-testid="nav-soggetti"
          >
            <span className="truncate flex items-center gap-2">
              <Icon.User className="w-4 h-4" />
              Soggetti
            </span>
          </button>
          <button
            type="button"
            onClick={() => setActiveView(DATI_AZIENDA_VIEW_KEY)}
            className={`w-full text-left px-5 py-3 font-mono text-sm transition-colors flex items-center justify-between gap-2 ${
              activeView === DATI_AZIENDA_VIEW_KEY
                ? "bg-[var(--c-accent)] text-white"
                : "hover:bg-white/10"
            }`}
            data-testid="nav-dati-azienda"
          >
            <span className="truncate flex items-center gap-2">
              <Icon.Building className="w-4 h-4" />
              Dati Azienda
            </span>
          </button>
          {isAdmin && (
            <button
              type="button"
              onClick={() => setActiveView(DRIVE_VIEW_KEY)}
              className={`w-full text-left px-5 py-3 font-mono text-sm transition-colors flex items-center justify-between gap-2 ${
                activeView === DRIVE_VIEW_KEY
                  ? "bg-[var(--c-accent)] text-white"
                  : "hover:bg-white/10"
              }`}
              data-testid="nav-drive"
            >
              <span className="truncate flex items-center gap-2">
                <Icon.Refresh className="w-4 h-4" />
                Sync Google Drive
              </span>
            </button>
          )}
        </nav>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {activeView === SETUP_VIEW_KEY ? (
          <SetupView />
        ) : activeView === AUTOMEZZI_VIEW_KEY ? (
          <AutomezziView />
        ) : activeView === SOGGETTI_VIEW_KEY ? (
          <SoggettiView />
        ) : activeView === DATI_AZIENDA_VIEW_KEY ? (
          <DatiAziendaView />
        ) : activeView === DRIVE_VIEW_KEY ? (
          <DriveSyncView />
        ) : activeView === RIMBORSI_VIEW_KEY ? (
          <RimborsiView
            session={session}
            importedTables={importedTables}
          />
        ) : (
        <>
        <div className="px-8 py-6 border-b border-[var(--c-border-soft)] flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--c-text-muted)]">
              {!hasImport
                ? "Nessun import"
                : activeMeta?.is_view
                  ? "Vista sintetica"
                  : "Tabella importata"}
            </div>
            <h2 className="font-display text-3xl truncate" data-testid="viewer-title">
              {activeView || "Documenti"}
            </h2>
            <div className="font-mono text-xs text-[var(--c-text-muted)] mt-1" data-testid="viewer-meta">
              {!hasImport
                ? "Nessun archivio importato"
                : data
                  ? isDocumenti
                    ? `${visibleRowCount.toLocaleString()} di ${data.total_rows.toLocaleString()} righe · ${visibleColumns.length} colonne`
                    : `${data.total_rows.toLocaleString()} righe · ${visibleColumns.length} colonne`
                  : "Caricamento…"}
            </div>
          </div>
          {hasImport && (
            <button
              type="button"
              onClick={downloadCsv}
              className="btn-ghost px-4 py-3 font-mono text-xs uppercase tracking-widest flex items-center gap-2 whitespace-nowrap"
              data-testid="download-csv-btn"
            >
              <Icon.Download className="w-4 h-4" />
              Scarica CSV
            </button>
          )}
        </div>

        {!hasImport ? (
          <div
            className="flex-1 flex items-center justify-center px-8 py-12 text-center"
            data-testid="viewer-empty"
          >
            <div className="max-w-md">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--c-text-muted)]">
                Lista vuota
              </div>
              <h3 className="font-display text-2xl mt-2">
                Nessun documento da mostrare.
              </h3>
              <p className="font-mono text-xs text-[var(--c-text-muted)] mt-3">
                {isAdmin ? (
                  <>I dati arrivano dalla sincronizzazione automatica con Google Drive. Vai su <strong>Sync Google Drive</strong> nel menu laterale.</>
                ) : (
                  <>Chiedi a un amministratore di importare l'archivio Access.</>
                )}
              </p>
            </div>
          </div>
        ) : (
          <>

        {/* Documenti filter bar */}
        {isDocumenti && data && (
          <div
            className="px-8 py-4 border-b border-[var(--c-border-soft)] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3"
            data-testid="documenti-filters"
          >
            <FilterField label="Tipo documento">
              <select
                value={fTipo}
                onChange={(e) => { setFTipo(e.target.value); setPage(0); }}
                className="filter-input"
                data-testid="filter-tipo"
              >
                <option value="">Tutti</option>
                <option value={FATTURE_GROUP_KEY}>FATTURE (differite + accompagnatorie)</option>
                {documentiOptions.tipi.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </FilterField>
            <FilterField label="Data da">
              <input
                type="date"
                value={fDataFrom}
                onChange={(e) => { setFDataFrom(e.target.value); setPage(0); }}
                className="filter-input"
                data-testid="filter-data-from"
              />
            </FilterField>
            <FilterField label="Data a">
              <input
                type="date"
                value={fDataTo}
                onChange={(e) => { setFDataTo(e.target.value); setPage(0); }}
                className="filter-input"
                data-testid="filter-data-to"
              />
            </FilterField>
            <FilterField label="Pagamento">
              <select
                value={fPagamento}
                onChange={(e) => { setFPagamento(e.target.value); setPage(0); }}
                className="filter-input"
                data-testid="filter-pagamento"
              >
                <option value="">Tutti</option>
                {documentiOptions.pagamenti.map((p) => (
                  <option key={p} value={p}>{p.length > 60 ? p.slice(0, 57) + "…" : p}</option>
                ))}
              </select>
            </FilterField>
            <FilterField label="Cliente">
              <input
                type="text"
                value={fCliente}
                onChange={(e) => { setFCliente(e.target.value); setPage(0); }}
                placeholder="Cerca anagrafica…"
                className="filter-input"
                data-testid="filter-cliente"
              />
            </FilterField>
          </div>
        )}

        {/* Grid */}
        <div className="flex-1 overflow-auto thin-scroll" data-testid="data-grid">
          {err ? (
            <div className="p-8 font-mono text-sm" style={{ color: "var(--c-error)" }}>
              {err}
            </div>
          ) : !data || loading ? (
            <div className="p-8">
              <div className="scanline-bar" />
              <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--c-text-muted)] mt-3">
                Caricamento righe…
              </div>
            </div>
          ) : (isDocumenti ? documentiPage?.length === 0 : data.rows.length === 0) ? (
            <div className="p-8 font-mono text-sm text-[var(--c-text-muted)]" data-testid="empty-rows">
              Nessuna riga corrisponde ai filtri.
            </div>
          ) : (
            <table className="w-full text-sm font-mono border-collapse">
              <thead>
                <tr>
                  {visibleColumns.map((c) => {
                    const isNumericCol = c === "Totale";
                    return (
                      <th
                        key={c}
                        className={`px-3 py-2 border-b border-[var(--c-text)] bg-[var(--c-surface)] sticky top-0 z-10 text-[10px] uppercase tracking-widest font-bold whitespace-nowrap ${
                          isNumericCol ? "text-right" : "text-left"
                        }`}
                      >
                        {COLUMN_LABELS[c] || c}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {(isDocumenti ? documentiPage : data.rows).map((row, ri) => (
                  <tr
                    key={ri}
                    className="hover:bg-[var(--c-surface)] transition-colors"
                    data-testid={`row-${ri}`}
                  >
                    {visibleColumnIndexes.map((origIdx, vi) => {
                      const colName = data.columns[origIdx];
                      const cell = row[origIdx];
                      const isTotale = colName === "Totale";
                      let displayCell;
                      if (cell === null || cell === "") {
                        displayCell = null;
                      } else if (isTotale) {
                        displayCell = formatItalianNumber(cell, 2);
                      } else {
                        const asString = String(cell);
                        const asDate = formatItalianDate(asString);
                        displayCell = asDate !== asString ? asDate : asString;
                      }
                      return (
                        <td
                          key={vi}
                          className={`px-3 py-2 border-b border-[var(--c-border-soft)] whitespace-nowrap max-w-[260px] overflow-hidden text-ellipsis ${
                            isTotale ? "text-right tabular-nums" : ""
                          }`}
                          title={cell === null ? "" : String(cell)}
                        >
                          {displayCell === null
                            ? <span className="text-[var(--c-text-muted)]">—</span>
                            : displayCell}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {data && visibleRowCount > PAGE_SIZE && (
          <div
            className="border-t border-[var(--c-border-soft)] px-8 py-3 flex items-center justify-between font-mono text-xs"
            data-testid="pagination"
          >
            <span className="text-[var(--c-text-muted)]">
              Pagina {page + 1} di {totalPages} · righe{" "}
              {(page * PAGE_SIZE + 1).toLocaleString()}–
              {Math.min((page + 1) * PAGE_SIZE, visibleRowCount).toLocaleString()}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="btn-ghost px-3 py-2 uppercase tracking-widest disabled:opacity-30"
                data-testid="prev-page-btn"
              >
                Prec
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="btn-ghost px-3 py-2 uppercase tracking-widest disabled:opacity-30"
                data-testid="next-page-btn"
              >
                Succ
              </button>
            </div>
          </div>
        )}
        </>
        )}
        </>
        )}
      </div>
    </main>
  );
}

/* ----------------------------------------------------------------------- */
/* Setup view: two locations (sede + magazzino)                              */
/* ----------------------------------------------------------------------- */
function SetupView() {
  const [setup, setSetup] = useState({
    sede: { indirizzo: "", cap: "", citta: "", provincia: "" },
    magazzino: { indirizzo: "", cap: "", citta: "", provincia: "" },
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/setup`)
      .then((r) => !cancelled && setSetup(r.data))
      .catch((e) => !cancelled && setErr(e?.message || "Errore caricamento"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const update = (loc, field, value) =>
    setSetup((prev) => ({
      ...prev,
      [loc]: { ...prev[loc], [field]: value },
    }));

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      const r = await axios.put(`${API}/setup`, setup);
      setSetup(r.data);
      toast.success("Sedi salvate.");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Salvataggio fallito";
      setErr(typeof msg === "string" ? msg : "Salvataggio fallito");
      toast.error(typeof msg === "string" ? msg : "Salvataggio fallito");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex-1 overflow-auto thin-scroll" data-testid="setup-view">
      <div className="px-8 py-6 border-b border-[var(--c-border-soft)] flex items-center justify-between gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--c-text-muted)]">
            Configurazione
          </div>
          <h2 className="font-display text-3xl">Sedi</h2>
          <div className="font-mono text-xs text-[var(--c-text-muted)] mt-1">
            Indirizzi di sede e magazzino · usati per calcolare le distanze dai clienti
          </div>
        </div>
        <button
          type="button"
          onClick={save}
          disabled={saving || loading}
          className="btn-primary px-6 py-3 font-black uppercase tracking-widest text-xs flex items-center gap-2 disabled:opacity-50"
          data-testid="setup-save-btn"
        >
          <Icon.Save className="w-4 h-4" />
          {saving ? "Salvataggio…" : "Salva"}
        </button>
      </div>

      <div className="px-8 py-6 grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl">
        <LocationCard
          title="Sede"
          subtitle="Sede operativa / ufficio"
          loc={setup.sede}
          onChange={(f, v) => update("sede", f, v)}
          disabled={loading}
          testIdPrefix="sede"
        />
        <LocationCard
          title="Magazzino"
          subtitle="Punto di carico / spedizione"
          loc={setup.magazzino}
          onChange={(f, v) => update("magazzino", f, v)}
          disabled={loading}
          testIdPrefix="magazzino"
        />
      </div>

      {err && (
        <div className="mx-8 mb-6 border border-[var(--c-error)] p-4 font-mono text-sm" style={{ color: "var(--c-error)" }}>
          {err}
        </div>
      )}
    </div>
  );
}

function LocationCard({ title, subtitle, loc, onChange, disabled, testIdPrefix }) {
  return (
    <section className="border border-[var(--c-text)]" data-testid={`setup-card-${testIdPrefix}`}>
      <header className="bg-[var(--c-text)] text-white px-5 py-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] opacity-60">
          {subtitle}
        </div>
        <div className="font-display text-xl">{title}</div>
      </header>
      <div className="p-5 grid grid-cols-1 sm:grid-cols-6 gap-4">
        <div className="sm:col-span-6">
          <SetupField
            label="Indirizzo"
            value={loc.indirizzo}
            onChange={(v) => onChange("indirizzo", v)}
            disabled={disabled}
            testid={`${testIdPrefix}-indirizzo`}
            placeholder="Via, n°"
          />
        </div>
        <div className="sm:col-span-2">
          <SetupField
            label="CAP"
            value={loc.cap}
            onChange={(v) => onChange("cap", v)}
            disabled={disabled}
            testid={`${testIdPrefix}-cap`}
            placeholder="24100"
            maxLength={5}
          />
        </div>
        <div className="sm:col-span-3">
          <SetupField
            label="Città"
            value={loc.citta}
            onChange={(v) => onChange("citta", v)}
            disabled={disabled}
            testid={`${testIdPrefix}-citta`}
            placeholder="Bergamo"
          />
        </div>
        <div className="sm:col-span-1">
          <SetupField
            label="Prov."
            value={loc.provincia}
            onChange={(v) => onChange("provincia", v.toUpperCase().slice(0, 2))}
            disabled={disabled}
            testid={`${testIdPrefix}-provincia`}
            placeholder="BG"
            maxLength={2}
          />
        </div>
      </div>
    </section>
  );
}

function SetupField({ label, value, onChange, disabled, testid, placeholder, maxLength }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-[var(--c-text-muted)]">
        {label}
      </span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder={placeholder}
        maxLength={maxLength}
        className="filter-input"
        data-testid={testid}
      />
    </label>
  );
}

/* ----------------------------------------------------------------------- */
/* Automezzi: CRUD                                                           */
/* ----------------------------------------------------------------------- */
const EMPTY_AUTOMEZZO = { automezzo: "", targa: "", costo_km: "" };

function AutomezziView() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {id?, automezzo, targa, costo_km} | null
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/automezzi`);
      setItems(r.data);
    } catch (e) {
      toast.error("Errore nel caricare gli automezzi.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const startCreate = () => setEditing({ ...EMPTY_AUTOMEZZO });
  const startEdit = (item) =>
    setEditing({
      id: item.id,
      automezzo: item.automezzo,
      targa: item.targa,
      costo_km: String(item.costo_km),
    });
  const cancel = () => setEditing(null);

  const handleSave = async () => {
    if (!editing) return;
    if (!editing.automezzo.trim() || !editing.targa.trim()) {
      toast.error("Automezzo e Targa sono obbligatori.");
      return;
    }
    const costo = Number(String(editing.costo_km).replace(",", "."));
    if (!Number.isFinite(costo) || costo < 0) {
      toast.error("Costo chilometrico non valido.");
      return;
    }
    setBusy(true);
    try {
      const body = {
        automezzo: editing.automezzo.trim(),
        targa: editing.targa.trim().toUpperCase(),
        costo_km: costo,
      };
      if (editing.id) {
        await axios.put(`${API}/automezzi/${editing.id}`, body);
        toast.success("Automezzo aggiornato.");
      } else {
        await axios.post(`${API}/automezzi`, body);
        toast.success("Automezzo creato.");
      }
      await refresh();
      setEditing(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Salvataggio fallito.");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Eliminare l'automezzo "${item.automezzo}" (${item.targa})?`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/automezzi/${item.id}`);
      toast.success("Automezzo eliminato.");
      await refresh();
    } catch (e) {
      toast.error("Eliminazione fallita.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex-1 overflow-auto thin-scroll" data-testid="automezzi-view">
      <div className="px-8 py-6 border-b border-[var(--c-border-soft)] flex items-center justify-between gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--c-text-muted)]">
            Configurazione
          </div>
          <h2 className="font-display text-3xl">Automezzi</h2>
          <div className="font-mono text-xs text-[var(--c-text-muted)] mt-1">
            Mezzi con costo chilometrico · usati per stimare il costo di trasporto verso il cliente
          </div>
        </div>
        <button
          type="button"
          onClick={startCreate}
          disabled={busy || editing != null}
          className="btn-primary px-5 py-3 font-black uppercase tracking-widest text-xs flex items-center gap-2 disabled:opacity-50"
          data-testid="automezzo-new-btn"
        >
          <Icon.Plus className="w-4 h-4" />
          Nuovo
        </button>
      </div>

      {editing && (
        <div className="px-8 py-5 border-b border-[var(--c-border-soft)] bg-[var(--c-surface)]" data-testid="automezzo-form">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--c-text-muted)] mb-3">
            {editing.id ? "Modifica automezzo" : "Nuovo automezzo"}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-end">
            <div className="sm:col-span-5">
              <SetupField
                label="Automezzo"
                value={editing.automezzo}
                onChange={(v) => setEditing((p) => ({ ...p, automezzo: v }))}
                placeholder="Iveco Daily 35S"
                testid="form-automezzo"
              />
            </div>
            <div className="sm:col-span-3">
              <SetupField
                label="Targa"
                value={editing.targa}
                onChange={(v) => setEditing((p) => ({ ...p, targa: v.toUpperCase() }))}
                placeholder="AB123CD"
                maxLength={10}
                testid="form-targa"
              />
            </div>
            <div className="sm:col-span-2">
              <SetupField
                label="Costo €/km"
                value={editing.costo_km}
                onChange={(v) => setEditing((p) => ({ ...p, costo_km: v.replace(/[^0-9.,]/g, "") }))}
                placeholder="0,85"
                testid="form-costo"
              />
              <a
                href="https://costikm.aci.it/home"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 mt-2 font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--c-text-muted)] hover:text-[var(--c-accent)] transition-colors"
                data-testid="aci-link"
                title="Apri il sito ACI in una nuova scheda per consultare i costi chilometrici ufficiali"
              >
                <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M14 4h6v6M20 4l-9 9M19 13v6a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h6" strokeLinecap="square" />
                </svg>
                Verifica costo ufficiale su ACI
              </a>
            </div>
            <div className="sm:col-span-2 flex gap-2 justify-end">
              <button
                type="button"
                onClick={cancel}
                disabled={busy}
                className="btn-ghost px-4 py-3 font-mono text-xs uppercase tracking-widest"
                data-testid="form-cancel"
              >
                Annulla
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={busy}
                className="btn-primary px-5 py-3 font-mono text-xs uppercase tracking-widest font-black"
                data-testid="form-save"
              >
                {busy ? "…" : "Salva"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="px-8 py-6">
        {loading ? (
          <div className="font-mono text-xs text-[var(--c-text-muted)]">
            Caricamento…
          </div>
        ) : items.length === 0 ? (
          <div className="border border-[var(--c-border-soft)] p-10 text-center font-mono text-sm text-[var(--c-text-muted)]" data-testid="automezzi-empty">
            Nessun automezzo. Clicca <span className="font-bold" style={{ color: "var(--c-accent)" }}>NUOVO</span> per inserirne uno.
          </div>
        ) : (
          <table className="w-full text-sm font-mono border-collapse">
            <thead>
              <tr>
                <th className="text-left px-3 py-2 border-b border-[var(--c-text)] bg-[var(--c-surface)] text-[10px] uppercase tracking-widest font-bold">Automezzo</th>
                <th className="text-left px-3 py-2 border-b border-[var(--c-text)] bg-[var(--c-surface)] text-[10px] uppercase tracking-widest font-bold">Targa</th>
                <th className="text-right px-3 py-2 border-b border-[var(--c-text)] bg-[var(--c-surface)] text-[10px] uppercase tracking-widest font-bold">Costo €/km</th>
                <th className="text-right px-3 py-2 border-b border-[var(--c-text)] bg-[var(--c-surface)] text-[10px] uppercase tracking-widest font-bold">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr
                  key={it.id}
                  className="hover:bg-[var(--c-surface)] transition-colors"
                  data-testid={`automezzo-row-${it.id}`}
                >
                  <td className="px-3 py-2 border-b border-[var(--c-border-soft)]">{it.automezzo}</td>
                  <td className="px-3 py-2 border-b border-[var(--c-border-soft)]">{it.targa}</td>
                  <td className="px-3 py-2 border-b border-[var(--c-border-soft)] text-right tabular-nums">
                    {formatItalianNumber(it.costo_km, 2)}
                  </td>
                  <td className="px-3 py-2 border-b border-[var(--c-border-soft)] text-right">
                    <div className="inline-flex gap-2">
                      <button
                        type="button"
                        onClick={() => startEdit(it)}
                        disabled={busy || editing != null}
                        className="btn-ghost px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest disabled:opacity-30"
                        data-testid={`automezzo-edit-${it.id}`}
                      >
                        Modifica
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(it)}
                        disabled={busy || editing != null}
                        className="btn-ghost px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest disabled:opacity-30"
                        style={{ borderColor: "var(--c-accent)", color: "var(--c-accent)" }}
                        data-testid={`automezzo-del-${it.id}`}
                      >
                        <Icon.Trash className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* =========================================================================
   Authentication & top-level router
   ========================================================================= */

const AuthCtx = createContext(null);
const useAuth = () => useContext(AuthCtx);

function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = checking, null = unauthenticated
  const [bootError, setBootError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const t = localStorage.getItem(AUTH_TOKEN_KEY);
      if (!t) { setUser(null); return; }
      try {
        const r = await axios.get(`${API}/auth/me`);
        if (!cancelled) setUser(r.data);
      } catch (e) {
        if (!cancelled) {
          if (e.response?.status === 401) {
            localStorage.removeItem(AUTH_TOKEN_KEY);
            setUser(null);
          } else {
            setBootError(e.message || "Errore di rete");
            setUser(null);
          }
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (username, password) => {
    const r = await axios.post(`${API}/auth/login`, { username, password });
    localStorage.setItem(AUTH_TOKEN_KEY, r.data.token);
    setUser(r.data.user);
    return r.data.user;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    setUser(null);
  }, []);

  const value = useMemo(() => ({ user, login, logout, bootError }), [user, login, logout, bootError]);
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

function LoginPage() {
  const { login } = useAuth();
  const version = useAppVersion();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await login(username.trim(), password);
    } catch (ex) {
      const d = ex?.response?.data?.detail;
      setErr(typeof d === "string" ? d : (ex.message || "Login fallito"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--c-bg)] px-6" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="flex items-center justify-center mb-3">
            <FlameHeart size={48} className="text-[var(--c-text)]" />
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--c-text-muted)] mb-2">
            ★ Accesso
          </div>
          <h1 className="font-display text-4xl">
            FUOCOIN <span className="text-[var(--c-accent)]">HUB</span>
          </h1>
          <div className="font-mono text-[11px] italic text-[var(--c-text-muted)] mt-1">
            Il gestionale del calore.
          </div>
          <div className="font-mono text-xs text-[var(--c-text-muted)] mt-1" data-testid="app-version-login">v{version}</div>
        </div>
        <form
          onSubmit={onSubmit}
          className="bg-white border border-[var(--c-text)] p-6 space-y-4"
          data-testid="login-form"
        >
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--c-text-muted)] block mb-1.5">
              Utente
            </label>
            <input
              type="text"
              autoFocus
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-3 py-2.5 bg-white border border-[var(--c-text)] font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[var(--c-accent)]"
              data-testid="login-username"
              required
            />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--c-text-muted)] block mb-1.5">
              Password
            </label>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2.5 bg-white border border-[var(--c-text)] font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[var(--c-accent)]"
              data-testid="login-password"
              required
            />
          </div>
          {err && (
            <div
              className="font-mono text-xs px-3 py-2 border"
              style={{ color: "var(--c-error)", borderColor: "var(--c-error)" }}
              data-testid="login-error"
            >
              {err}
            </div>
          )}
          <button
            type="submit"
            disabled={busy}
            className="w-full px-4 py-3 font-mono text-xs uppercase tracking-widest bg-[var(--c-accent)] text-white hover:opacity-90 disabled:opacity-40 transition-opacity"
            data-testid="login-submit"
          >
            {busy ? "Accesso…" : "Entra"}
          </button>
        </form>
      </div>
    </div>
  );
}

function HomeMenu({ onPick }) {
  const { user, logout } = useAuth();
  const version = useAppVersion();
  return (
    <div className="min-h-screen bg-[var(--c-bg)] flex flex-col" data-testid="home-menu">
      {/* Top bar */}
      <header className="px-8 py-5 flex items-center justify-between bg-[#0a0a0a] text-white border-b border-[#0a0a0a]">
        <div className="flex items-center gap-3">
          <FlameHeart size={36} />
          <div>
            <div className="font-display text-2xl leading-none">
              FUOCOIN <span className="text-[var(--c-accent)]">HUB</span>
              <span className="font-mono text-xs text-white/50 ml-3" data-testid="app-version-home">v{version}</span>
            </div>
            <div className="font-mono text-[10px] italic text-white/60 mt-0.5">
              Il gestionale del calore.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="font-mono text-xs">
            <span className="text-white/60">Utente · </span>
            <span className="font-bold" data-testid="home-username">{user?.username}</span>
            {user?.role === "admin" && (
              <span className="ml-2 inline-block px-2 py-0.5 bg-[var(--c-accent)] text-white text-[9px] uppercase tracking-widest">
                Admin
              </span>
            )}
          </div>
          {user?.role === "admin" && (
            <button
              type="button"
              onClick={() => onPick("users")}
              className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest border border-white/40 text-white hover:bg-white/10 transition-colors"
              data-testid="home-users-btn"
            >
              Utenti
            </button>
          )}
          <button
            type="button"
            onClick={logout}
            className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest border border-[var(--c-accent)] text-[var(--c-accent)] hover:bg-[var(--c-accent)] hover:text-white transition-colors"
            data-testid="home-logout"
          >
            Esci
          </button>
        </div>
      </header>

      {/* Hero / module grid */}
      <main className="flex-1 px-8 py-12 max-w-5xl w-full mx-auto">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--c-text-muted)] mb-3">
          ▌ Gestioni
        </div>
        <h1 className="font-display text-5xl mb-2">Menu principale</h1>
        <p className="font-mono text-sm text-[var(--c-text-muted)] mb-12">
          Seleziona la gestione su cui vuoi lavorare.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="home-modules-grid">
          {(user?.modules || []).includes("viaggi") && (
            <ModuleCard
              title="Spese di Viaggio"
              description="Documenti, sedi, automezzi, soggetti e calcolo distanze sui clienti."
              onClick={() => onPick("viaggi")}
              testid="card-viaggi"
              available
            />
          )}
          {(user?.modules || []).includes("presenze") && (
            <ModuleCard
              title="Presenze / Ore"
              description="Dipendenti, profilo orario, tabellone mensile, assenze e straordinarie."
              onClick={() => onPick("presenze")}
              testid="card-presenze"
              available
            />
          )}
          {(user?.modules || []).includes("consulente") && (
            <ModuleCard
              title="Documenti"
              description="Scarica in un unico file ZIP le note spese e i report presenze da inviare al consulente."
              onClick={() => onPick("consulente")}
              testid="card-consulente"
              available
            />
          )}
          {(!user?.modules || user.modules.length === 0) && (
            <div className="sm:col-span-2 lg:col-span-3 border border-dashed border-[var(--c-border-soft)] bg-[var(--c-surface)] p-6 font-mono text-sm text-[var(--c-text-muted)]" data-testid="home-no-modules">
              Nessun modulo è attualmente abilitato per il tuo utente. Contatta un amministratore.
            </div>
          )}
          <ModuleCard
            title="In arrivo"
            description="Un nuovo modulo gestionale sarà aggiunto qui."
            onClick={() => {}}
            testid="card-coming-1"
          />
        </div>
      </main>

      <footer className="px-8 py-5 border-t border-[var(--c-border-soft)] bg-[var(--c-surface)]">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <FlameHeart size={20} className="text-[var(--c-text)]" />
              <span className="font-display text-sm">
                FUOCOIN <span className="text-[var(--c-accent)]">HUB</span>
              </span>
            </div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--c-text-muted)]">
              Fuoco IN srl · Barzana (BG), Via Sorte 25 · 035 555893 · info@fuocoin.com
            </div>
          </div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--c-text-muted)] sm:text-right">
            v{version} · Accesso protetto da JWT
          </div>
        </div>
      </footer>
    </div>
  );
}

function ModuleCard({ title, description, onClick, testid, available }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!available}
      className={`text-left p-6 border ${
        available
          ? "border-[var(--c-text)] hover:border-[var(--c-accent)] hover:shadow-[6px_6px_0_var(--c-accent)] transition-all bg-white cursor-pointer"
          : "border-dashed border-[var(--c-border-soft)] bg-[var(--c-surface)] opacity-60 cursor-not-allowed"
      }`}
      data-testid={testid}
    >
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--c-text-muted)] mb-2">
        {available ? "▸ Modulo" : "◇ Disponibile a breve"}
      </div>
      <div className="font-display text-2xl mb-2">{title}</div>
      <div className="font-mono text-xs text-[var(--c-text-muted)] leading-relaxed">{description}</div>
      {available && (
        <div className="mt-4 font-mono text-[10px] uppercase tracking-widest text-[var(--c-accent)]">
          Apri →
        </div>
      )}
    </button>
  );
}

function App() {
  return (
    <AuthProvider>
      <Toaster position="top-right" richColors />
      <AppShell />
    </AuthProvider>
  );
}

function AppShell() {
  const { user } = useAuth();
  const [route, setRoute] = useState("home"); // 'home' | 'viaggi' | 'users'

  if (user === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--c-bg)] font-mono text-xs text-[var(--c-text-muted)]">
        Verifica sessione…
      </div>
    );
  }
  if (!user) return <LoginPage />;

  const isAdmin = user?.role === "admin";
  const userModules = user?.modules || [];
  const canAccess = (mod) => isAdmin || userModules.includes(mod);

  if (route === "viaggi") {
    if (!canAccess("viaggi")) return <HomeMenu onPick={setRoute} />;
    return <GestioneViaggi goHome={() => setRoute("home")} isAdmin={isAdmin} />;
  }
  if (route === "presenze") {
    if (!canAccess("presenze")) return <HomeMenu onPick={setRoute} />;
    return <GestionePresenze goHome={() => setRoute("home")} isAdmin={isAdmin} />;
  }
  if (route === "consulente") {
    if (!canAccess("consulente")) return <HomeMenu onPick={setRoute} />;
    return <GestioneConsulente goHome={() => setRoute("home")} isAdmin={isAdmin} />;
  }
  if (route === "users") {
    if (!isAdmin) return <HomeMenu onPick={setRoute} />;
    return <UsersAdmin goHome={() => setRoute("home")} />;
  }
  return <HomeMenu onPick={setRoute} />;
}

/* =========================================================================
   Users admin (admin only)
   ========================================================================= */

function UsersAdmin({ goHome }) {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {id?, username, role, password?}
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/users`);
      setItems(r.data);
    } catch (e) {
      toast.error("Impossibile caricare gli utenti.");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const startNew = () => setEditing({ username: "", role: "user", password: "", modules: [] });
  const startEdit = (u) => setEditing({ id: u.id, username: u.username, role: u.role, password: "", modules: [...(u.modules || [])] });
  const cancel = () => setEditing(null);

  const toggleEditingModule = (mod) => {
    setEditing((p) => {
      const cur = new Set(p.modules || []);
      if (cur.has(mod)) cur.delete(mod);
      else cur.add(mod);
      return { ...p, modules: Array.from(cur) };
    });
  };

  const toggleUserModule = async (u, mod) => {
    const cur = new Set(u.modules || []);
    if (cur.has(mod)) cur.delete(mod);
    else cur.add(mod);
    try {
      await axios.put(`${API}/users/${u.id}`, { modules: Array.from(cur) });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aggiornamento fallito.");
    }
  };

  const save = async () => {
    if (!editing) return;
    if (!editing.username.trim()) { toast.error("Username obbligatorio."); return; }
    setBusy(true);
    try {
      if (editing.id) {
        await axios.put(`${API}/users/${editing.id}`, {
          username: editing.username.trim(), role: editing.role,
          modules: editing.modules || [],
        });
        if (editing.password) {
          await axios.post(`${API}/users/${editing.id}/password`, { password: editing.password });
        }
        toast.success("Utente aggiornato.");
      } else {
        if (!editing.password || editing.password.length < 4) {
          toast.error("Password obbligatoria (min 4 caratteri)."); setBusy(false); return;
        }
        await axios.post(`${API}/users`, {
          username: editing.username.trim(), password: editing.password, role: editing.role,
          modules: editing.modules || [],
        });
        toast.success("Utente creato.");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Salvataggio fallito.");
    } finally { setBusy(false); }
  };

  const handleDelete = async (u) => {
    if (u.id === me.id) { toast.error("Non puoi eliminare te stesso."); return; }
    if (!window.confirm(`Eliminare l'utente ${u.username}?`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/users/${u.id}`);
      toast.success("Utente eliminato.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eliminazione fallita.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[var(--c-bg)]" data-testid="users-view">
      <header className="px-8 py-4 flex items-center justify-between border-b border-[var(--c-border-soft)]">
        <div className="flex items-center gap-3">
          <button onClick={goHome} className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest border border-[var(--c-text)] hover:bg-[var(--c-surface)]" data-testid="users-back">← Home</button>
          <div className="font-display text-xl">Utenti</div>
        </div>
        <button onClick={startNew} disabled={editing != null} className="px-4 py-2 font-mono text-xs uppercase tracking-widest bg-[var(--c-accent)] text-white hover:opacity-90 disabled:opacity-40 flex items-center gap-2" data-testid="users-new">
          <Icon.Plus className="w-4 h-4" /> Nuovo
        </button>
      </header>

      <main className="max-w-4xl mx-auto p-8">
        {editing && (
          <div className="bg-[var(--c-surface)] border border-[var(--c-border-soft)] p-5 mb-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--c-text-muted)] mb-3">
              {editing.id ? "Modifica utente" : "Nuovo utente"}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <SetupField label="Username" value={editing.username} onChange={(v) => setEditing((p) => ({ ...p, username: v }))} testid="form-username" maxLength={40} />
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--c-text-muted)] mb-1.5">Ruolo</div>
                <select
                  value={editing.role}
                  onChange={(e) => setEditing((p) => ({ ...p, role: e.target.value }))}
                  className="w-full px-3 py-2.5 bg-white border border-[var(--c-text)] font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[var(--c-accent)]"
                  data-testid="form-role"
                >
                  <option value="user">Utente</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <SetupField
                  label={editing.id ? "Nuova password (lascia vuoto per non cambiarla)" : "Password"}
                  value={editing.password}
                  onChange={(v) => setEditing((p) => ({ ...p, password: v }))}
                  testid="form-password"
                  placeholder="••••••••"
                  maxLength={80}
                />
              </div>
              <div className="sm:col-span-2">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--c-text-muted)] mb-2">
                  Moduli abilitati {editing.role === "admin" && <span className="text-[var(--c-accent)]">(admin = accesso completo)</span>}
                </div>
                <div className="flex flex-wrap gap-3">
                  {[
                    { id: "viaggi", label: "Spese di Viaggio" },
                    { id: "presenze", label: "Presenze / Ore" },
                    { id: "consulente", label: "Documenti" },
                  ].map((m) => {
                    const checked = editing.role === "admin" || (editing.modules || []).includes(m.id);
                    const disabled = editing.role === "admin";
                    return (
                      <label
                        key={m.id}
                        className={`flex items-center gap-2 px-3 py-2 border ${
                          checked ? "border-[var(--c-accent)] bg-white" : "border-[var(--c-border-soft)] bg-white"
                        } ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:bg-[var(--c-surface)]"}`}
                        data-testid={`form-module-${m.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={disabled}
                          onChange={() => toggleEditingModule(m.id)}
                        />
                        <span className="font-mono text-xs">{m.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              <div className="sm:col-span-2 flex gap-2 justify-end">
                <button type="button" onClick={cancel} disabled={busy} className="btn-ghost px-4 py-3 font-mono text-xs uppercase tracking-widest" data-testid="form-cancel">Annulla</button>
                <button type="button" onClick={save} disabled={busy} className="px-4 py-3 font-mono text-xs uppercase tracking-widest bg-[var(--c-accent)] text-white hover:opacity-90 disabled:opacity-40" data-testid="form-save">Salva</button>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="font-mono text-xs text-[var(--c-text-muted)]">Caricamento…</div>
        ) : (
          <table className="w-full font-mono text-sm">
            <thead>
              <tr>
                <th className="text-left px-3 py-2 border-b border-[var(--c-text)] bg-[var(--c-surface)] text-[10px] uppercase tracking-widest font-bold">Utente</th>
                <th className="text-left px-3 py-2 border-b border-[var(--c-text)] bg-[var(--c-surface)] text-[10px] uppercase tracking-widest font-bold">Ruolo</th>
                <th className="text-left px-3 py-2 border-b border-[var(--c-text)] bg-[var(--c-surface)] text-[10px] uppercase tracking-widest font-bold">Moduli</th>
                <th className="text-right px-3 py-2 border-b border-[var(--c-text)] bg-[var(--c-surface)] text-[10px] uppercase tracking-widest font-bold">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => {
                const isUserAdmin = u.role === "admin";
                return (
                  <tr key={u.id} className="hover:bg-[var(--c-surface)]" data-testid={`user-row-${u.id}`}>
                    <td className="px-3 py-2 border-b border-[var(--c-border-soft)]">{u.username}{u.id === me.id && <span className="ml-2 text-[10px] uppercase text-[var(--c-text-muted)]">(tu)</span>}</td>
                    <td className="px-3 py-2 border-b border-[var(--c-border-soft)]">{u.role}</td>
                    <td className="px-3 py-2 border-b border-[var(--c-border-soft)]">
                      {isUserAdmin ? (
                        <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--c-text-muted)]" data-testid={`user-modules-${u.id}`}>
                          ★ tutti (admin)
                        </span>
                      ) : (
                        <div className="flex flex-wrap gap-2" data-testid={`user-modules-${u.id}`}>
                          {[
                            { id: "viaggi", label: "Viaggi" },
                            { id: "presenze", label: "Presenze" },
                            { id: "consulente", label: "Documenti" },
                          ].map((m) => {
                            const on = (u.modules || []).includes(m.id);
                            return (
                              <button
                                key={m.id}
                                type="button"
                                onClick={() => toggleUserModule(u, m.id)}
                                disabled={busy || editing != null}
                                className={`px-2 py-1 font-mono text-[10px] uppercase tracking-widest border transition-colors disabled:opacity-30 ${
                                  on
                                    ? "bg-[var(--c-accent)] text-white border-[var(--c-accent)]"
                                    : "bg-white text-[var(--c-text-muted)] border-[var(--c-border-soft)] hover:border-[var(--c-text)]"
                                }`}
                                data-testid={`user-mod-${u.id}-${m.id}`}
                                title={on ? `Disattiva ${m.label}` : `Attiva ${m.label}`}
                              >
                                {on ? "● " : "○ "}{m.label}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 border-b border-[var(--c-border-soft)] text-right">
                      <div className="inline-flex gap-2">
                        <button type="button" onClick={() => startEdit(u)} disabled={busy || editing != null} className="btn-ghost px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest disabled:opacity-30" data-testid={`user-edit-${u.id}`}>Modifica</button>
                        <button type="button" onClick={() => handleDelete(u)} disabled={busy || editing != null || u.id === me.id} className="btn-ghost px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest disabled:opacity-30" style={{ borderColor: "var(--c-accent)", color: "var(--c-accent)" }} data-testid={`user-del-${u.id}`}>
                          <Icon.Trash className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </main>
    </div>
  );
}

/* =========================================================================
   Dati Azienda
   ========================================================================= */

function DatiAziendaView() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/dati-azienda`).then((r) => setData(r.data)).catch(() => setData({
      ragione_sociale: "", indirizzo: "", cap: "", citta: "", provincia: "", piva: "",
    }));
  }, []);

  if (!data) return <div className="flex-1 p-8 font-mono text-xs text-[var(--c-text-muted)]">Caricamento…</div>;

  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/dati-azienda`, data);
      toast.success("Dati azienda salvati.");
    } catch (e) {
      toast.error("Salvataggio fallito.");
    } finally { setBusy(false); }
  };

  return (
    <div className="flex-1 overflow-auto p-8" data-testid="dati-azienda-view">
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--c-text-muted)]">Configurazione</div>
          <h2 className="font-display text-3xl">Dati Azienda</h2>
          <p className="font-mono text-xs text-[var(--c-text-muted)] mt-1">
            Stampati nell'intestazione di ogni Nota Spese.
          </p>
        </div>
        <div className="bg-[var(--c-surface)] border border-[var(--c-border-soft)] p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <SetupField label="Ragione sociale" value={data.ragione_sociale} onChange={(v) => setData({ ...data, ragione_sociale: v })} testid="da-rs" maxLength={120} />
          </div>
          <div className="sm:col-span-2">
            <SetupField label="Indirizzo" value={data.indirizzo} onChange={(v) => setData({ ...data, indirizzo: v })} testid="da-indir" maxLength={120} />
          </div>
          <SetupField label="CAP" value={data.cap} onChange={(v) => setData({ ...data, cap: v })} testid="da-cap" maxLength={10} />
          <SetupField label="Città" value={data.citta} onChange={(v) => setData({ ...data, citta: v })} testid="da-citta" maxLength={80} />
          <SetupField label="Provincia" value={data.provincia} onChange={(v) => setData({ ...data, provincia: v.toUpperCase() })} testid="da-prov" maxLength={4} />
          <SetupField label="Partita IVA" value={data.piva} onChange={(v) => setData({ ...data, piva: v })} testid="da-piva" maxLength={20} />
          <div className="sm:col-span-2 flex justify-end">
            <button type="button" onClick={save} disabled={busy} className="px-4 py-3 font-mono text-xs uppercase tracking-widest bg-[var(--c-accent)] text-white hover:opacity-90 disabled:opacity-40" data-testid="da-save">
              Salva
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* =========================================================================
   Drive Sync — admin-only view to configure Google Drive auto import
   ========================================================================= */
function DriveSyncView() {
  const [status, setStatus] = useState(null);
  const [folderId, setFolderId] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [intervalH, setIntervalH] = useState(4);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [importing, setImporting] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [logs, setLogs] = useState([]);

  const loadStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/drive/status`);
      setStatus(r.data);
      setFolderId(r.data.folder_id || "");
      setEnabled(!!r.data.enabled);
      setIntervalH(r.data.interval_hours || 4);
      setNotifyEmail(r.data.notify_email || "");
    } catch (e) {
      toast.error("Impossibile caricare lo stato Drive.");
    }
  }, []);

  const loadLogs = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/drive/logs?limit=30`);
      setLogs(r.data || []);
    } catch (e) {
      setLogs([]);
    }
  }, []);

  useEffect(() => {
    loadStatus();
    loadLogs();
  }, [loadStatus, loadLogs]);

  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/drive/settings`, {
        folder_id: folderId.trim(),
        enabled,
        interval_hours: Number(intervalH) || 4,
        notify_email: notifyEmail.trim(),
      });
      toast.success("Impostazioni salvate.");
      await loadStatus();
    } catch (e) {
      toast.error("Salvataggio fallito.");
    } finally { setBusy(false); }
  };

  const verify = async () => {
    setBusy(true);
    setVerifyResult(null);
    try {
      // Save first so the backend has the latest folder_id
      await axios.put(`${API}/drive/settings`, {
        folder_id: folderId.trim(),
        enabled,
        interval_hours: Number(intervalH) || 4,
        notify_email: notifyEmail.trim(),
      });
      const r = await axios.post(`${API}/drive/verify`);
      setVerifyResult(r.data);
      toast.success(`Cartella OK: trovati ${r.data.files_count} file.`);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Errore";
      toast.error(`Verifica fallita: ${msg}`);
      setVerifyResult({ error: msg });
    } finally { setBusy(false); }
  };

  const importNow = async () => {
    if (!window.confirm("Avviare ora un import dal file più recente della cartella Drive?")) return;
    setBusy(true);
    setImporting(true);
    // Start auto-refresh of logs every 5s while importing
    const refreshInterval = setInterval(() => { loadLogs(); }, 5000);
    try {
      const r = await axios.post(`${API}/drive/import-now`);
      const d = r.data || {};
      if (d.skipped) {
        toast.warning(d.message || "Nessun file da importare.");
      } else if (d.file_name) {
        toast.success(`Import avviato: ${d.file_name} (${(d.size || 0).toLocaleString()} bytes).`);
      } else {
        toast.warning(d.message || "Nessun file .rdy/.accdb trovato nella cartella.");
      }
      await loadStatus();
      await loadLogs();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Errore";
      toast.error(`Import fallito: ${msg}`);
      await loadLogs();
    } finally {
      clearInterval(refreshInterval);
      setImporting(false);
      setBusy(false);
    }
  };

  if (!status) {
    return <div className="flex-1 p-8 font-mono text-xs text-[var(--c-text-muted)]">Caricamento…</div>;
  }

  const fmtBytes = (n) => {
    const x = Number(n) || 0;
    if (x < 1024) return `${x} B`;
    if (x < 1024 * 1024) return `${(x / 1024).toFixed(1)} KB`;
    return `${(x / 1024 / 1024).toFixed(1)} MB`;
  };

  return (
    <div className="flex-1 overflow-auto p-8" data-testid="drive-sync-view">
      <div className="max-w-3xl mx-auto space-y-6">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--c-text-muted)]">Amministrazione</div>
          <h2 className="font-display text-3xl">Sync Google Drive</h2>
          <p className="font-mono text-xs text-[var(--c-text-muted)] mt-1">
            Importa automaticamente il file .rdy dalla cartella Google Drive condivisa con il Service Account.
          </p>
        </div>

        {/* Service Account status */}
        <div className="bg-white border border-[var(--c-text)] p-5">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--c-text-muted)] mb-2">▌ Service Account</div>
          {status.configured ? (
            <>
              <div className="font-mono text-sm">
                <span className="text-green-700">●</span> Configurato
              </div>
              <div className="mt-2">
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--c-text-muted)]">Email da condividere su Drive</div>
                <div className="font-mono text-sm bg-[var(--c-surface)] px-2 py-1 mt-1 border border-[var(--c-border-soft)] select-all break-all" data-testid="drive-sa-email">
                  {status.service_account_email || "(non disponibile)"}
                </div>
                <div className="font-mono text-[11px] text-[var(--c-text-muted)] mt-2 leading-relaxed">
                  Apri la cartella Drive che contiene il file .rdy → click destro → <strong>Condividi</strong> → incolla l'email qui sopra → ruolo "Visualizzatore" → Invia.
                </div>
              </div>
            </>
          ) : (
            <div className="font-mono text-sm">
              <span className="text-red-600">●</span> Service Account NON configurato.
              <div className="font-mono text-[11px] text-[var(--c-text-muted)] mt-2 leading-relaxed">
                Il server deve avere la variabile d'ambiente <code>GOOGLE_SERVICE_ACCOUNT_JSON</code> con il contenuto del file JSON Service Account creato su Google Cloud Console. Chiedi al gestore deploy di impostarla.
              </div>
            </div>
          )}
        </div>

        {/* Settings */}
        <div className="bg-white border border-[var(--c-text)] p-5 space-y-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--c-text-muted)]">▌ Configurazione</div>
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--c-text-muted)]">ID Cartella Drive</span>
            <input
              type="text"
              value={folderId}
              onChange={(e) => setFolderId(e.target.value)}
              placeholder="es. 1aBcDeFgHiJkLmNoPq..."
              className="input-base font-mono text-sm w-full mt-1"
              data-testid="drive-folder-id"
            />
            <span className="font-mono text-[10px] text-[var(--c-text-muted)] mt-1 block">
              Copia l'ID dalla URL Drive (la parte dopo <code>/folders/</code>).
            </span>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--c-text-muted)]">Intervallo (ore)</span>
              <input
                type="number"
                min="0.5"
                step="0.5"
                value={intervalH}
                onChange={(e) => setIntervalH(e.target.value)}
                className="input-base font-mono text-sm w-full mt-1"
                data-testid="drive-interval"
              />
            </label>
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--c-text-muted)]">Email notifica errori</span>
              <input
                type="email"
                value={notifyEmail}
                onChange={(e) => setNotifyEmail(e.target.value)}
                placeholder="amministratore@dominio.it"
                className="input-base font-mono text-sm w-full mt-1"
                data-testid="drive-notify-email"
              />
            </label>
          </div>
          <label className="flex items-center gap-2 font-mono text-sm cursor-pointer mt-2">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              data-testid="drive-enabled"
            />
            <span>Abilita sync automatico in background</span>
          </label>
          <div className="flex flex-wrap gap-2 pt-2">
            <button
              type="button"
              onClick={save}
              disabled={busy}
              className="px-4 py-2 font-mono text-xs uppercase tracking-widest bg-[var(--c-accent)] text-white hover:opacity-90 disabled:opacity-40"
              data-testid="drive-save"
            >
              Salva
            </button>
            <button
              type="button"
              onClick={verify}
              disabled={busy || !folderId.trim() || !status.configured}
              className="px-4 py-2 font-mono text-xs uppercase tracking-widest border border-[var(--c-text)] hover:bg-[var(--c-surface)] disabled:opacity-40"
              data-testid="drive-verify"
            >
              Verifica cartella
            </button>
            <button
              type="button"
              onClick={importNow}
              disabled={busy || !folderId.trim() || !status.configured}
              className="px-4 py-2 font-mono text-xs uppercase tracking-widest border border-[var(--c-accent)] text-[var(--c-accent)] hover:bg-[var(--c-accent)] hover:text-white disabled:opacity-40 flex items-center gap-2"
              data-testid="drive-import-now"
            >
              {importing && (
                <svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M21 12a9 9 0 1 1-6.219-8.56" strokeLinecap="round" />
                </svg>
              )}
              {importing ? "Importazione…" : "Importa ora"}
            </button>
          </div>
        </div>

        {/* Verify result */}
        {verifyResult && !verifyResult.error && (
          <div className="bg-white border border-green-600 p-5" data-testid="drive-verify-result">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-green-700 mb-2">▌ Cartella verificata</div>
            <div className="font-mono text-sm">
              <strong>{verifyResult.folder?.name}</strong> · {verifyResult.files_count} file .rdy/.accdb
            </div>
            {verifyResult.files && verifyResult.files.length > 0 && (
              <table className="w-full mt-3 font-mono text-xs">
                <thead>
                  <tr className="text-left text-[var(--c-text-muted)] border-b border-[var(--c-border-soft)]">
                    <th className="py-1">Nome</th>
                    <th className="py-1">Modificato</th>
                    <th className="py-1 text-right">Dimensione</th>
                  </tr>
                </thead>
                <tbody>
                  {verifyResult.files.map((f, i) => (
                    <tr key={f.id} className={i === 0 ? "bg-yellow-50" : ""}>
                      <td className="py-1">{f.name} {i === 0 && <span className="text-[10px] text-[var(--c-accent)]">(più recente)</span>}</td>
                      <td className="py-1">{f.modifiedTime ? new Date(f.modifiedTime).toLocaleString("it-IT") : "—"}</td>
                      <td className="py-1 text-right">{fmtBytes(f.size)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Last import */}
        {status.last_imported_file_name && (
          <div className="bg-[var(--c-surface)] border border-[var(--c-border-soft)] p-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--c-text-muted)] mb-2">▌ Ultimo import</div>
            <div className="font-mono text-sm">
              <strong>{status.last_imported_file_name}</strong> · {fmtBytes(status.last_imported_size)}
            </div>
            <div className="font-mono text-[11px] text-[var(--c-text-muted)] mt-1">
              {status.last_imported_at ? new Date(status.last_imported_at).toLocaleString("it-IT") : "—"}
            </div>
          </div>
        )}

        {/* Logs */}
        <div className="bg-white border border-[var(--c-text)] p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--c-text-muted)]">▌ Log Sync</div>
            <button
              type="button"
              onClick={loadLogs}
              className="font-mono text-[10px] uppercase tracking-widest text-[var(--c-text-muted)] hover:text-[var(--c-accent)]"
              data-testid="drive-logs-refresh"
            >
              Aggiorna
            </button>
          </div>
          {logs.length === 0 ? (
            <div className="font-mono text-xs text-[var(--c-text-muted)]">Nessun log ancora registrato.</div>
          ) : (
            <table className="w-full font-mono text-xs">
              <thead>
                <tr className="text-left text-[var(--c-text-muted)] border-b border-[var(--c-border-soft)]">
                  <th className="py-1">Data</th>
                  <th className="py-1">Stato</th>
                  <th className="py-1">Tipo</th>
                  <th className="py-1">File</th>
                  <th className="py-1">Messaggio</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => {
                  const color = l.status === "success" ? "text-green-700"
                              : l.status === "error" ? "text-red-600"
                              : "text-[var(--c-text-muted)]";
                  return (
                    <tr key={l.id} className="border-b border-[var(--c-border-soft)]">
                      <td className="py-1 whitespace-nowrap">{l.created_at ? new Date(l.created_at).toLocaleString("it-IT") : "—"}</td>
                      <td className={`py-1 ${color}`}>● {l.status}</td>
                      <td className="py-1">{l.trigger}</td>
                      <td className="py-1 truncate max-w-[160px]" title={l.file_name}>{l.file_name || "—"}</td>
                      <td className="py-1 text-[var(--c-text-muted)] truncate max-w-[280px]" title={l.message}>{l.message}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}


/* =========================================================================
   Rimborsi — lista schede + dettaglio + aggiungi riga + PDF
   ========================================================================= */

function RimborsiView({ session, importedTables }) {
  const [schede, setSchede] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openScheda, setOpenScheda] = useState(null); // detail object or null
  const [creating, setCreating] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/rimborsi/schede`);
      setSchede(r.data);
    } catch (e) {
      toast.error("Impossibile caricare le schede.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const openDetail = async (sid) => {
    try {
      const r = await axios.get(`${API}/rimborsi/schede/${sid}`);
      setOpenScheda(r.data);
    } catch (e) { toast.error("Impossibile aprire la scheda."); }
  };

  const closeDetail = () => { setOpenScheda(null); reload(); };

  const handleDelete = async (s) => {
    if (!window.confirm(`Eliminare la scheda ${s.numero}?`)) return;
    try {
      await axios.delete(`${API}/rimborsi/schede/${s.id}`);
      toast.success("Scheda eliminata.");
      reload();
    } catch (e) { toast.error("Eliminazione fallita."); }
  };

  if (openScheda) {
    return (
      <RimborsoDetail
        scheda={openScheda}
        setScheda={setOpenScheda}
        onClose={closeDetail}
        session={session}
        importedTables={importedTables}
      />
    );
  }

  return (
    <div className="flex-1 overflow-auto p-8" data-testid="rimborsi-view">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--c-text-muted)]">Rimborsi</div>
            <h2 className="font-display text-3xl">Schede Rimborsi Spese</h2>
          </div>
          <button type="button" onClick={() => setCreating(true)} className="px-4 py-3 font-mono text-xs uppercase tracking-widest bg-[var(--c-accent)] text-white hover:opacity-90 flex items-center gap-2" data-testid="scheda-new">
            <Icon.Plus className="w-4 h-4" /> Nuova Scheda
          </button>
        </div>

        {creating && (
          <NewSchedaForm onCancel={() => setCreating(false)} onCreated={(s) => {
            setCreating(false);
            reload();
            openDetail(s.id);
          }} />
        )}

        {loading ? (
          <div className="font-mono text-xs text-[var(--c-text-muted)]">Caricamento…</div>
        ) : schede.length === 0 ? (
          <div className="text-center py-12 border border-dashed border-[var(--c-border-soft)] font-mono text-sm text-[var(--c-text-muted)]">
            Nessuna scheda. Clicca <strong className="text-[var(--c-accent)]">NUOVA SCHEDA</strong>.
          </div>
        ) : (
          <table className="w-full font-mono text-sm">
            <thead>
              <tr>
                <th className="text-left px-3 py-2 border-b border-[var(--c-text)] bg-[var(--c-surface)] text-[10px] uppercase tracking-widest font-bold">Numero</th>
                <th className="text-left px-3 py-2 border-b border-[var(--c-text)] bg-[var(--c-surface)] text-[10px] uppercase tracking-widest font-bold">Data Emissione</th>
                <th className="text-left px-3 py-2 border-b border-[var(--c-text)] bg-[var(--c-surface)] text-[10px] uppercase tracking-widest font-bold">Competenza</th>
                <th className="text-left px-3 py-2 border-b border-[var(--c-text)] bg-[var(--c-surface)] text-[10px] uppercase tracking-widest font-bold">Soggetto</th>
                <th className="text-left px-3 py-2 border-b border-[var(--c-text)] bg-[var(--c-surface)] text-[10px] uppercase tracking-widest font-bold">Automezzo</th>
                <th className="text-right px-3 py-2 border-b border-[var(--c-text)] bg-[var(--c-surface)] text-[10px] uppercase tracking-widest font-bold">Righe</th>
                <th className="text-right px-3 py-2 border-b border-[var(--c-text)] bg-[var(--c-surface)] text-[10px] uppercase tracking-widest font-bold">Tot. €</th>
                <th className="text-right px-3 py-2 border-b border-[var(--c-text)] bg-[var(--c-surface)] text-[10px] uppercase tracking-widest font-bold">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {schede.map((s) => (
                <tr key={s.id} className="hover:bg-[var(--c-surface)]" data-testid={`scheda-row-${s.id}`}>
                  <td className="px-3 py-2 border-b border-[var(--c-border-soft)] font-bold">{s.numero}</td>
                  <td className="px-3 py-2 border-b border-[var(--c-border-soft)] tabular-nums">{formatItalianDate(s.data_emissione)}</td>
                  <td className="px-3 py-2 border-b border-[var(--c-border-soft)]">{itMonth(s.mese_competenza)} {s.anno_competenza}</td>
                  <td className="px-3 py-2 border-b border-[var(--c-border-soft)]">{s.soggetto?.cognome} {s.soggetto?.nome}</td>
                  <td className="px-3 py-2 border-b border-[var(--c-border-soft)]">{s.automezzo?.automezzo} ({s.automezzo?.targa})</td>
                  <td className="px-3 py-2 border-b border-[var(--c-border-soft)] text-right tabular-nums">{s.righe_count}</td>
                  <td className="px-3 py-2 border-b border-[var(--c-border-soft)] text-right tabular-nums font-bold">{formatItalianNumber(s.totale_euro, 2)}</td>
                  <td className="px-3 py-2 border-b border-[var(--c-border-soft)] text-right">
                    <div className="inline-flex gap-2">
                      <button onClick={() => openDetail(s.id)} className="btn-ghost px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest" data-testid={`scheda-open-${s.id}`}>Apri</button>
                      <button onClick={() => handleDelete(s)} className="btn-ghost px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest" style={{ borderColor: "var(--c-accent)", color: "var(--c-accent)" }} data-testid={`scheda-del-${s.id}`}>
                        <Icon.Trash className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function itMonth(m) {
  const names = ["", "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
  return names[m] || String(m);
}

function NewSchedaForm({ onCancel, onCreated }) {
  const today = new Date().toISOString().slice(0, 10);
  const now = new Date();
  const [form, setForm] = useState({
    data_emissione: today,
    mese_competenza: now.getMonth() + 1,
    anno_competenza: now.getFullYear(),
    automezzo_id: "",
    soggetto_id: "",
  });
  const [automezzi, setAutomezzi] = useState([]);
  const [soggetti, setSoggetti] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/automezzi`),
      axios.get(`${API}/soggetti`),
    ]).then(([a, s]) => { setAutomezzi(a.data); setSoggetti(s.data); });
  }, []);

  const save = async () => {
    if (!form.automezzo_id || !form.soggetto_id) {
      toast.error("Seleziona automezzo e soggetto."); return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/rimborsi/schede`, {
        ...form,
        mese_competenza: Number(form.mese_competenza),
        anno_competenza: Number(form.anno_competenza),
      });
      toast.success(`Scheda ${r.data.numero} creata.`);
      onCreated(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Creazione fallita."); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-[var(--c-surface)] border border-[var(--c-border-soft)] p-5 mb-6" data-testid="scheda-form">
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--c-text-muted)] mb-3">Nuova scheda</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--c-text-muted)] mb-1.5">Data emissione</div>
          <input type="date" value={form.data_emissione} onChange={(e) => setForm({ ...form, data_emissione: e.target.value })} className="w-full px-3 py-2.5 bg-white border border-[var(--c-text)] font-mono text-sm" data-testid="sf-data" />
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--c-text-muted)] mb-1.5">Mese competenza</div>
          <select value={form.mese_competenza} onChange={(e) => setForm({ ...form, mese_competenza: e.target.value })} className="w-full px-3 py-2.5 bg-white border border-[var(--c-text)] font-mono text-sm" data-testid="sf-mese">
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => <option key={m} value={m}>{itMonth(m)}</option>)}
          </select>
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--c-text-muted)] mb-1.5">Anno competenza</div>
          <input type="number" min={2020} max={2100} value={form.anno_competenza} onChange={(e) => setForm({ ...form, anno_competenza: e.target.value })} className="w-full px-3 py-2.5 bg-white border border-[var(--c-text)] font-mono text-sm" data-testid="sf-anno" />
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--c-text-muted)] mb-1.5">Automezzo</div>
          <select value={form.automezzo_id} onChange={(e) => setForm({ ...form, automezzo_id: e.target.value })} className="w-full px-3 py-2.5 bg-white border border-[var(--c-text)] font-mono text-sm" data-testid="sf-auto">
            <option value="">— seleziona —</option>
            {automezzi.map((a) => <option key={a.id} value={a.id}>{a.automezzo} ({a.targa}) · €{a.costo_km}/km</option>)}
          </select>
        </div>
        <div className="sm:col-span-2">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--c-text-muted)] mb-1.5">Soggetto</div>
          <select value={form.soggetto_id} onChange={(e) => setForm({ ...form, soggetto_id: e.target.value })} className="w-full px-3 py-2.5 bg-white border border-[var(--c-text)] font-mono text-sm" data-testid="sf-sogg">
            <option value="">— seleziona —</option>
            {soggetti.map((s) => <option key={s.id} value={s.id}>{s.cognome} {s.nome}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2 lg:col-span-3 flex justify-end gap-2">
          <button onClick={onCancel} disabled={busy} className="btn-ghost px-4 py-3 font-mono text-xs uppercase tracking-widest" data-testid="sf-cancel">Annulla</button>
          <button onClick={save} disabled={busy} className="px-4 py-3 font-mono text-xs uppercase tracking-widest bg-[var(--c-accent)] text-white hover:opacity-90 disabled:opacity-40" data-testid="sf-save">Crea</button>
        </div>
      </div>
    </div>
  );
}

function PdfPreviewModal({ url, filename, title, onClose }) {
  const containerRef = useRef(null);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const renderTaskRef = useRef([]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // ---- Render PDF onto canvases inside the modal ----
  useEffect(() => {
    let cancelled = false;
    const render = async () => {
      try {
        setLoading(true);
        setError(null);
        // Lazy-load PDF.js (legacy build = max browser compat)
        const pdfjs = await import("pdfjs-dist/legacy/build/pdf");
        // Worker via CDN — same exact version as the imported lib
        try {
          // eslint-disable-next-line no-undef
          pdfjs.GlobalWorkerOptions.workerSrc =
            "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.10.38/pdf.worker.min.mjs";
        } catch (_) {}
        const buf = await (await fetch(url)).arrayBuffer();
        if (cancelled) return;
        const pdf = await pdfjs.getDocument({ data: buf }).promise;
        if (cancelled) return;
        setPages(pdf.numPages);

        const container = containerRef.current;
        if (!container) return;
        container.innerHTML = "";

        const dpr = Math.min(window.devicePixelRatio || 1, 3);
        // Target visual width — go wide for landscape PDFs to keep text sharp.
        const targetCssWidth = Math.max(
          400,
          Math.min(1400, (container.clientWidth || 800) - 32)
        );

        for (let n = 1; n <= pdf.numPages; n++) {
          if (cancelled) return;
          const page = await pdf.getPage(n);
          const baseVp = page.getViewport({ scale: 1 });
          const scale = (targetCssWidth / baseVp.width);
          const viewport = page.getViewport({ scale: scale * dpr });

          const canvas = document.createElement("canvas");
          canvas.width = Math.round(viewport.width);
          canvas.height = Math.round(viewport.height);
          canvas.style.width = `${Math.round(viewport.width / dpr)}px`;
          canvas.style.height = `${Math.round(viewport.height / dpr)}px`;
          canvas.style.display = "block";
          canvas.style.margin = "0 auto 16px";
          canvas.style.background = "white";
          canvas.style.boxShadow = "0 1px 3px rgba(0,0,0,0.25)";
          canvas.setAttribute("data-testid", `pdf-page-${n}`);
          container.appendChild(canvas);

          const ctx = canvas.getContext("2d");
          const task = page.render({ canvasContext: ctx, viewport });
          renderTaskRef.current.push(task);
          await task.promise;
        }
        if (!cancelled) setLoading(false);
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || "Errore caricamento PDF");
          setLoading(false);
        }
      }
    };
    render();
    return () => {
      cancelled = true;
      try {
        renderTaskRef.current.forEach((t) => { try { t.cancel(); } catch (_) {} });
      } catch (_) {}
      renderTaskRef.current = [];
    };
  }, [url]);

  const handlePrint = async () => {
    // Strategy: re-render the PDF pages onto high-DPI canvases, inject them
    // inside a hidden print container with `@media print` CSS that sets
    // `@page { size: 297mm 210mm; margin: 0 }` and hides everything else.
    // Then call window.print() on the current window. This works in every
    // browser (no popups, no cross-origin issues, no blob-URL download) and
    // prints at ~300 DPI raster which is indistinguishable from the source
    // PDF for office printing.
    if (!url) { toast.error("PDF non disponibile."); return; }
    const t = toast.loading("Preparazione stampa…");
    try {
      const pdfjs = await import("pdfjs-dist/legacy/build/pdf");
      try {
        // eslint-disable-next-line no-undef
        pdfjs.GlobalWorkerOptions.workerSrc =
          "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.10.38/pdf.worker.min.mjs";
      } catch (_) { /* ignore */ }
      const buf = await (await fetch(url)).arrayBuffer();
      const pdf = await pdfjs.getDocument({ data: buf }).promise;

      // Clean up any previous print container
      const old = document.getElementById("__pdf_print_container__");
      if (old) old.remove();
      const oldStyle = document.getElementById("__pdf_print_style__");
      if (oldStyle) oldStyle.remove();

      // Print CSS — landscape A4, hide everything else
      const style = document.createElement("style");
      style.id = "__pdf_print_style__";
      style.textContent = `
@media print {
  @page { size: 297mm 210mm; margin: 0; }
  html, body { margin: 0 !important; padding: 0 !important; background: white !important; }
  body > *:not(#__pdf_print_container__) { display: none !important; }
  #__pdf_print_container__ { display: block !important; position: absolute !important;
    left: 0 !important; top: 0 !important; width: 297mm !important; }
  #__pdf_print_container__ .pdf-print-page {
    width: 297mm !important; height: 210mm !important;
    page-break-after: always;
    display: block !important;
    overflow: hidden !important;
  }
  #__pdf_print_container__ .pdf-print-page:last-child { page-break-after: auto; }
  #__pdf_print_container__ canvas {
    width: 297mm !important; height: 210mm !important;
    display: block !important;
  }
}
@media screen { #__pdf_print_container__ { display: none !important; } }
`;
      document.head.appendChild(style);

      const container = document.createElement("div");
      container.id = "__pdf_print_container__";
      document.body.appendChild(container);

      // Render each page at print resolution.
      // A4 landscape at 200 DPI = ~2339 × 1654 px — very sharp on most printers.
      const PRINT_DPI = 200;
      for (let n = 1; n <= pdf.numPages; n++) {
        const page = await pdf.getPage(n);
        const baseVp = page.getViewport({ scale: 1 });
        // baseVp is in PDF points (1 pt = 1/72 inch). Convert to print DPI.
        const scale = PRINT_DPI / 72;
        const viewport = page.getViewport({ scale });
        const pageWrap = document.createElement("div");
        pageWrap.className = "pdf-print-page";
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(viewport.width);
        canvas.height = Math.round(viewport.height);
        const ctx = canvas.getContext("2d");
        await page.render({ canvasContext: ctx, viewport }).promise;
        pageWrap.appendChild(canvas);
        container.appendChild(pageWrap);
      }

      toast.dismiss(t);

      // Cleanup after the user closes the print dialog (Chrome/Firefox fire
      // `afterprint`; Safari sometimes doesn't, hence the fallback timeout).
      const cleanup = () => {
        try { container.remove(); } catch (_) { /* ignore */ }
        try { style.remove(); } catch (_) { /* ignore */ }
        window.removeEventListener("afterprint", cleanup);
      };
      window.addEventListener("afterprint", cleanup);
      setTimeout(cleanup, 60000); // safety net

      // Trigger the print dialog of the current window
      setTimeout(() => window.print(), 50);
    } catch (e) {
      toast.dismiss(t);
      toast.error("Stampa non riuscita. Prova a scaricare il PDF.");
    }
  };

  const handleDownload = () => {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "nota-spese.pdf";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
      data-testid="pdf-preview-modal"
    >
      <div
        className="bg-[var(--c-bg)] border border-[var(--c-text)] w-full max-w-7xl h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--c-text)] gap-3">
          <div className="font-display text-xl truncate">{title || "Anteprima PDF"}</div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--c-text-muted)] whitespace-nowrap">
            {loading ? "Caricamento…" : error ? "Errore" : `${pages} pag.`}
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <button
              onClick={handlePrint}
              disabled={loading || !!error}
              className="px-4 py-2 font-mono text-[11px] uppercase tracking-widest border border-[var(--c-text)] hover:bg-[var(--c-surface)] flex items-center gap-2 disabled:opacity-40"
              data-testid="pdf-modal-print"
            >
              <Icon.Printer className="w-4 h-4" /> Stampa
            </button>
            <button
              onClick={handleDownload}
              className="px-4 py-2 font-mono text-[11px] uppercase tracking-widest bg-[var(--c-accent)] text-white hover:opacity-90 flex items-center gap-2"
              data-testid="pdf-modal-download"
            >
              <Icon.Download className="w-4 h-4" /> Scarica PDF
            </button>
            <button
              onClick={onClose}
              className="px-3 py-2 font-mono text-[11px] uppercase tracking-widest border border-[var(--c-border-soft)] hover:bg-[var(--c-surface)]"
              data-testid="pdf-modal-close"
              aria-label="Chiudi"
            >
              ✕
            </button>
          </div>
        </div>
        <div className="flex-1 bg-neutral-200 overflow-auto p-4">
          {error && (
            <div className="text-center font-mono text-sm text-red-600 py-12" data-testid="pdf-preview-error">
              Impossibile renderizzare il PDF: {error}
            </div>
          )}
          {loading && !error && (
            <div className="text-center font-mono text-xs text-[var(--c-text-muted)] py-12">
              Generazione anteprima…
            </div>
          )}
          <div ref={containerRef} data-testid="pdf-pages-container" />
        </div>
      </div>
    </div>
  );
}

function RimborsoDetail({ scheda, setScheda, onClose, session, importedTables }) {
  const [picker, setPicker] = useState(false);
  const [freeRow, setFreeRow] = useState(null); // { data, descrizione, km } | null
  const [pdfPreview, setPdfPreview] = useState(null); // { url, filename } | null
  const [pdfLoading, setPdfLoading] = useState(false);
  const [editHeader, setEditHeader] = useState(null); // null | { data_emissione, mese_competenza, anno_competenza, soggetto_id, automezzo_id }
  const [soggettiOpt, setSoggettiOpt] = useState([]);
  const [automezziOpt, setAutomezziOpt] = useState([]);
  const [savingHeader, setSavingHeader] = useState(false);

  const openEditHeader = async () => {
    try {
      const [s, a] = await Promise.all([
        axios.get(`${API}/soggetti`),
        axios.get(`${API}/automezzi`),
      ]);
      setSoggettiOpt(s.data || []);
      setAutomezziOpt(a.data || []);
      setEditHeader({
        data_emissione: scheda.data_emissione || "",
        mese_competenza: scheda.mese_competenza,
        anno_competenza: scheda.anno_competenza,
        soggetto_id: scheda.soggetto?.id || "",
        automezzo_id: scheda.automezzo?.id || "",
      });
    } catch (e) {
      toast.error("Caricamento anagrafiche fallito.");
    }
  };

  const saveHeader = async () => {
    if (!editHeader) return;
    setSavingHeader(true);
    try {
      const r = await axios.patch(`${API}/rimborsi/schede/${scheda.id}`, {
        data_emissione: editHeader.data_emissione,
        mese_competenza: parseInt(editHeader.mese_competenza, 10),
        anno_competenza: parseInt(editHeader.anno_competenza, 10),
        soggetto_id: editHeader.soggetto_id,
        automezzo_id: editHeader.automezzo_id,
      });
      setScheda(r.data);
      setEditHeader(null);
      toast.success("Testata aggiornata.");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Aggiornamento testata fallito.";
      toast.error(msg);
    } finally {
      setSavingHeader(false);
    }
  };

  const handleDeleteRiga = async (riga) => {
    if (!window.confirm("Eliminare la riga?")) return;
    try {
      const r = await axios.delete(`${API}/rimborsi/schede/${scheda.id}/righe/${riga.id}`);
      setScheda(r.data);
      toast.success("Riga eliminata.");
    } catch (e) { toast.error("Eliminazione fallita."); }
  };

  const handleRigaDateChange = async (riga, newDate) => {
    if (!newDate || newDate === riga.data) return;
    try {
      const r = await axios.patch(
        `${API}/rimborsi/schede/${scheda.id}/righe/${riga.id}`,
        { data: newDate }
      );
      setScheda(r.data);
    } catch (e) { toast.error("Aggiornamento data fallito."); }
  };

  const handleRigaDescrizioneChange = async (riga, newDescr) => {
    const next = (newDescr || "").trim();
    if (next === (riga.descrizione || "").trim()) return;
    try {
      const r = await axios.patch(
        `${API}/rimborsi/schede/${scheda.id}/righe/${riga.id}`,
        { descrizione: next }
      );
      setScheda(r.data);
    } catch (e) { toast.error("Aggiornamento descrizione fallito."); }
  };

  const openFreeRow = () => {
    setFreeRow({
      data: new Date().toISOString().slice(0, 10),
      descrizione: "",
      km: "",
    });
  };

  const submitFreeRow = async () => {
    if (!freeRow) return;
    const km = parseFloat(String(freeRow.km).replace(",", "."));
    if (!Number.isFinite(km) || km < 0) {
      toast.error("Inserisci un valore valido per i km.");
      return;
    }
    if (!freeRow.descrizione.trim() && km <= 0) {
      toast.error("Inserisci una descrizione o i km.");
      return;
    }
    try {
      const r = await axios.post(`${API}/rimborsi/schede/${scheda.id}/righe`, {
        doc_key: null,
        data: freeRow.data,
        descrizione: freeRow.descrizione.trim(),
        km,
      });
      setScheda(r.data);
      setFreeRow(null);
      toast.success("Riga libera aggiunta.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aggiunta fallita.");
    }
  };

  const openPdfPreview = async () => {
    if (pdfLoading) return;
    setPdfLoading(true);
    try {
      const r = await axios.get(`${API}/rimborsi/schede/${scheda.id}/pdf`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const filename = `nota-spese-${(scheda.numero || "").replace(/\//g, "-")}.pdf`;
      setPdfPreview({ url, filename });
    } catch (e) {
      toast.error("Impossibile generare il PDF.");
    } finally {
      setPdfLoading(false);
    }
  };

  const closePdfPreview = () => {
    if (pdfPreview?.url) {
      try { URL.revokeObjectURL(pdfPreview.url); } catch (_) {}
    }
    setPdfPreview(null);
  };

  useEffect(() => {
    return () => {
      // cleanup on unmount
      if (pdfPreview?.url) {
        try { URL.revokeObjectURL(pdfPreview.url); } catch (_) {}
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mese = itMonth(scheda.mese_competenza);

  return (
    <div className="flex-1 overflow-auto p-8" data-testid="rimborso-detail">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <button onClick={onClose} className="font-mono text-[10px] uppercase tracking-widest text-[var(--c-text-muted)] hover:text-[var(--c-accent)] mb-1" data-testid="detail-back">
              ← Torna alle schede
            </button>
            <h2 className="font-display text-3xl">Nota Spese {scheda.numero}</h2>
            <p className="font-mono text-xs text-[var(--c-text-muted)] mt-1">
              Emessa il {formatItalianDate(scheda.data_emissione)} · Competenza {mese} {scheda.anno_competenza}
            </p>
            <p className="font-mono text-xs mt-2">
              <strong>{scheda.soggetto?.cognome} {scheda.soggetto?.nome}</strong> · {scheda.automezzo?.automezzo} ({scheda.automezzo?.targa}) · €{scheda.automezzo?.costo_km}/km
            </p>
            <button
              type="button"
              onClick={openEditHeader}
              className="mt-2 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest border border-[var(--c-text)] hover:bg-[var(--c-surface)] inline-flex items-center gap-1.5"
              data-testid="scheda-edit-header"
            >
              <Icon.Settings className="w-3 h-3" /> Modifica testata
            </button>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setPicker(true)} className="px-4 py-3 font-mono text-xs uppercase tracking-widest bg-[var(--c-accent)] text-white hover:opacity-90 flex items-center gap-2" data-testid="riga-new">
              <Icon.Plus className="w-4 h-4" /> Aggiungi da documenti
            </button>
            <button onClick={openFreeRow} className="px-4 py-3 font-mono text-xs uppercase tracking-widest border border-[var(--c-accent)] text-[var(--c-accent)] hover:bg-[var(--c-accent)] hover:text-white flex items-center gap-2" data-testid="riga-free">
              <Icon.Plus className="w-4 h-4" /> Riga libera
            </button>
            <button onClick={openPdfPreview} disabled={pdfLoading} className="px-4 py-3 font-mono text-xs uppercase tracking-widest border border-[var(--c-text)] hover:bg-[var(--c-surface)] flex items-center gap-2 disabled:opacity-40" data-testid="pdf-preview-open">
              <Icon.Printer className="w-4 h-4" /> {pdfLoading ? "Generazione…" : "Anteprima PDF"}
            </button>
          </div>
        </div>

        {editHeader && (
          <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" data-testid="edit-header-modal">
            <div className="bg-white border-2 border-[var(--c-text)] w-full max-w-lg">
              <div className="p-4 border-b border-[var(--c-border-soft)] flex items-center justify-between">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--c-text-muted)]">Nota Spese {scheda.numero}</div>
                  <div className="font-display text-xl">Modifica testata</div>
                </div>
                <button onClick={() => setEditHeader(null)} className="btn-ghost px-3 py-2 font-mono text-[10px] uppercase tracking-widest">Annulla</button>
              </div>
              <div className="p-4 space-y-3">
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--c-text-muted)]">Data emissione</span>
                  <input
                    type="date"
                    value={editHeader.data_emissione || ""}
                    onChange={(e) => setEditHeader({ ...editHeader, data_emissione: e.target.value })}
                    className="w-full px-3 py-2 border border-[var(--c-text)] font-mono text-sm mt-1"
                    data-testid="edit-header-data-emissione"
                  />
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--c-text-muted)]">Mese competenza</span>
                    <select
                      value={editHeader.mese_competenza}
                      onChange={(e) => setEditHeader({ ...editHeader, mese_competenza: parseInt(e.target.value, 10) })}
                      className="w-full px-3 py-2 border border-[var(--c-text)] font-mono text-sm mt-1"
                      data-testid="edit-header-mese"
                    >
                      {["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"].map((m, i) => (
                        <option key={i+1} value={i+1}>{m}</option>
                      ))}
                    </select>
                  </label>
                  <label className="block">
                    <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--c-text-muted)]">Anno competenza</span>
                    <input
                      type="number"
                      min="2000" max="2100"
                      value={editHeader.anno_competenza}
                      onChange={(e) => setEditHeader({ ...editHeader, anno_competenza: e.target.value })}
                      className="w-full px-3 py-2 border border-[var(--c-text)] font-mono text-sm mt-1"
                      data-testid="edit-header-anno"
                    />
                  </label>
                </div>
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--c-text-muted)]">Soggetto</span>
                  <select
                    value={editHeader.soggetto_id || ""}
                    onChange={(e) => setEditHeader({ ...editHeader, soggetto_id: e.target.value })}
                    className="w-full px-3 py-2 border border-[var(--c-text)] font-mono text-sm mt-1"
                    data-testid="edit-header-soggetto"
                  >
                    {soggettiOpt.map((s) => (
                      <option key={s.id} value={s.id}>{s.cognome} {s.nome}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--c-text-muted)]">Automezzo</span>
                  <select
                    value={editHeader.automezzo_id || ""}
                    onChange={(e) => setEditHeader({ ...editHeader, automezzo_id: e.target.value })}
                    className="w-full px-3 py-2 border border-[var(--c-text)] font-mono text-sm mt-1"
                    data-testid="edit-header-automezzo"
                  >
                    {automezziOpt.map((a) => (
                      <option key={a.id} value={a.id}>{a.automezzo} ({a.targa}) · €{a.costo_km}/km</option>
                    ))}
                  </select>
                </label>
                <p className="font-mono text-[10px] text-[var(--c-text-muted)]">
                  Cambiando l'automezzo, tutti gli importi (€) delle righe verranno ricalcolati sulla nuova tariffa €/km.
                </p>
              </div>
              <div className="p-4 border-t border-[var(--c-border-soft)] flex items-center justify-end gap-2">
                <button
                  onClick={() => setEditHeader(null)}
                  className="px-4 py-2 font-mono text-xs uppercase tracking-widest border border-[var(--c-text)] hover:bg-[var(--c-surface)]"
                  data-testid="edit-header-cancel"
                >
                  Annulla
                </button>
                <button
                  onClick={saveHeader}
                  disabled={savingHeader}
                  className="px-4 py-2 font-mono text-xs uppercase tracking-widest bg-[var(--c-accent)] text-white hover:opacity-90 disabled:opacity-40"
                  data-testid="edit-header-save"
                >
                  {savingHeader ? "Salvataggio…" : "Salva"}
                </button>
              </div>
            </div>
          </div>
        )}

        {freeRow && (
          <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" data-testid="free-row-modal">
            <div className="bg-white border-2 border-[var(--c-text)] w-full max-w-lg">
              <div className="p-4 border-b border-[var(--c-border-soft)] flex items-center justify-between">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--c-text-muted)]">Nota Spese</div>
                  <div className="font-display text-xl">Riga libera</div>
                </div>
                <button onClick={() => setFreeRow(null)} className="btn-ghost px-3 py-2 font-mono text-[10px] uppercase tracking-widest">Annulla</button>
              </div>
              <div className="p-4 space-y-3">
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--c-text-muted)]">Data</span>
                  <input
                    type="date"
                    value={freeRow.data}
                    onChange={(e) => setFreeRow((p) => ({ ...p, data: e.target.value }))}
                    className="w-full px-3 py-2 bg-white border border-[var(--c-text)] font-mono text-sm mt-1"
                    data-testid="free-row-data"
                  />
                </label>
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--c-text-muted)]">Descrizione</span>
                  <input
                    type="text"
                    value={freeRow.descrizione}
                    onChange={(e) => setFreeRow((p) => ({ ...p, descrizione: e.target.value }))}
                    placeholder="es. Sopralluogo cliente sconosciuto / Sostituzione mezzo aziendale"
                    className="w-full px-3 py-2 bg-white border border-[var(--c-text)] font-mono text-sm mt-1"
                    data-testid="free-row-descr"
                  />
                </label>
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--c-text-muted)]">Km percorsi</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={freeRow.km}
                    onChange={(e) => setFreeRow((p) => ({ ...p, km: e.target.value }))}
                    placeholder="0,00"
                    className="w-full px-3 py-2 bg-white border border-[var(--c-text)] font-mono text-sm tabular-nums mt-1"
                    data-testid="free-row-km"
                  />
                  <span className="font-mono text-[10px] text-[var(--c-text-muted)]">
                    Totale = km × €{scheda.automezzo?.costo_km}/km = <strong className="text-[var(--c-accent)]">
                      € {formatItalianNumber((2 * (parseFloat(String(freeRow.km).replace(",", ".")) || 0) * (scheda.automezzo?.costo_km || 0)), 2)}
                    </strong>
                  </span>
                </label>
              </div>
              <div className="p-4 border-t border-[var(--c-border-soft)] flex justify-end gap-2">
                <button onClick={() => setFreeRow(null)} className="btn-ghost px-4 py-2 font-mono text-xs uppercase tracking-widest">Annulla</button>
                <button onClick={submitFreeRow} className="px-4 py-2 font-mono text-xs uppercase tracking-widest bg-[var(--c-accent)] text-white hover:opacity-90" data-testid="free-row-save">
                  Salva riga
                </button>
              </div>
            </div>
          </div>
        )}

        {picker && (
          <DocPicker
            scheda={scheda}
            session={session}
            importedTables={importedTables}
            onClose={() => setPicker(false)}
            onAdded={(updated) => { setScheda(updated); setPicker(false); }}
          />
        )}

        {pdfPreview && (
          <PdfPreviewModal
            url={pdfPreview.url}
            filename={pdfPreview.filename}
            title={`Nota Spese ${scheda.numero}`}
            onClose={closePdfPreview}
          />
        )}

        {/* Totals card */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="border border-[var(--c-text)] p-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--c-text-muted)]">Righe</div>
            <div className="font-display text-3xl">{scheda.righe_count}</div>
          </div>
          <div className="border border-[var(--c-text)] p-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--c-text-muted)]">Totale Km A/R</div>
            <div className="font-display text-3xl tabular-nums">{formatItalianNumber((scheda.totale_km || 0) * 2, 2)}</div>
          </div>
          <div className="border-2 border-[var(--c-accent)] p-4 bg-[var(--c-accent)] text-white">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] opacity-80">Totale Euro</div>
            <div className="font-display text-3xl tabular-nums" data-testid="total-euro">€ {formatItalianNumber(scheda.totale_euro, 2)}</div>
          </div>
        </div>

        {(!scheda.righe || scheda.righe.length === 0) ? (
          <div className="text-center py-12 border border-dashed border-[var(--c-border-soft)] font-mono text-sm text-[var(--c-text-muted)]">
            Nessuna riga. Clicca <strong className="text-[var(--c-accent)]">AGGIUNGI RIGA</strong>.
          </div>
        ) : (
          <table className="w-full font-mono text-sm">
            <thead>
              <tr>
                <th className="text-left px-3 py-2 border-b border-[var(--c-text)] bg-[var(--c-surface)] text-[10px] uppercase tracking-widest font-bold">Data doc.</th>
                <th className="text-left px-3 py-2 border-b border-[var(--c-text)] bg-[var(--c-surface)] text-[10px] uppercase tracking-widest font-bold">Data viaggio</th>
                <th className="text-left px-3 py-2 border-b border-[var(--c-text)] bg-[var(--c-surface)] text-[10px] uppercase tracking-widest font-bold">Cliente</th>
                <th className="text-left px-3 py-2 border-b border-[var(--c-text)] bg-[var(--c-surface)] text-[10px] uppercase tracking-widest font-bold">Destinazione</th>
                <th className="text-left px-3 py-2 border-b border-[var(--c-text)] bg-[var(--c-surface)] text-[10px] uppercase tracking-widest font-bold">Origine</th>
                <th className="text-right px-3 py-2 border-b border-[var(--c-text)] bg-[var(--c-surface)] text-[10px] uppercase tracking-widest font-bold">Km tratta</th>
                <th className="text-right px-3 py-2 border-b border-[var(--c-text)] bg-[var(--c-surface)] text-[10px] uppercase tracking-widest font-bold">Km tot. A/R</th>
                <th className="text-right px-3 py-2 border-b border-[var(--c-text)] bg-[var(--c-surface)] text-[10px] uppercase tracking-widest font-bold">€</th>
                <th className="text-right px-3 py-2 border-b border-[var(--c-text)] bg-[var(--c-surface)] text-[10px] uppercase tracking-widest font-bold"></th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const counts = {};
                (scheda.righe || []).forEach((r) => {
                  if (r.free) return;
                  const key = (r.anagrafica || "").trim().toUpperCase();
                  if (!key) return;
                  counts[key] = (counts[key] || 0) + 1;
                });
                return scheda.righe.map((r) => {
                  const key = (r.anagrafica || "").trim().toUpperCase();
                  const isDup = !r.free && key && counts[key] > 1;
                  return (
                <tr key={r.id} className={`${isDup ? "bg-red-200" : ""} hover:bg-[var(--c-surface)]`} data-testid={`riga-row-${r.id}`}>
                  <td className="px-3 py-2 border-b border-[var(--c-border-soft)] tabular-nums text-[var(--c-text-muted)]">
                    {r.free ? "—" : formatItalianDate(r.data_doc || r.data)}
                  </td>
                  <td className="px-3 py-2 border-b border-[var(--c-border-soft)] tabular-nums">
                    <input
                      type="date"
                      defaultValue={r.data ? String(r.data).slice(0, 10) : ""}
                      onBlur={(e) => handleRigaDateChange(r, e.target.value)}
                      className="px-1.5 py-0.5 border border-[var(--c-accent)] font-mono text-[12px] tabular-nums"
                      data-testid={`riga-data-${r.id}`}
                    />
                  </td>
                  <td className="px-3 py-2 border-b border-[var(--c-border-soft)]">
                    {r.free ? <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--c-accent)]">LIBERA</span> : r.anagrafica}
                  </td>
                  <td className="px-3 py-2 border-b border-[var(--c-border-soft)] text-[var(--c-text-muted)]">
                    {r.free ? (
                      <input
                        type="text"
                        defaultValue={r.descrizione || ""}
                        onBlur={(e) => handleRigaDescrizioneChange(r, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") e.currentTarget.blur();
                        }}
                        placeholder="Descrizione…"
                        className="w-full px-1.5 py-0.5 border border-[var(--c-accent)] font-mono text-[12px] bg-white"
                        data-testid={`riga-descr-${r.id}`}
                      />
                    ) : (
                      [r.indirizzo, r.cap, r.citta, r.provincia && `(${r.provincia})`].filter(Boolean).join(" ")
                    )}
                  </td>
                  <td className="px-3 py-2 border-b border-[var(--c-border-soft)] uppercase text-[10px]">{r.free ? "—" : r.origine}</td>
                  <td className="px-3 py-2 border-b border-[var(--c-border-soft)] text-right tabular-nums text-[var(--c-text-muted)]">{formatItalianNumber(r.km, 2)}</td>
                  <td className="px-3 py-2 border-b border-[var(--c-border-soft)] text-right tabular-nums">{formatItalianNumber((r.km || 0) * 2, 2)}</td>
                  <td className="px-3 py-2 border-b border-[var(--c-border-soft)] text-right tabular-nums font-bold">{formatItalianNumber(r.euro, 2)}</td>
                  <td className="px-3 py-2 border-b border-[var(--c-border-soft)] text-right">
                    <button onClick={() => handleDeleteRiga(r)} className="btn-ghost px-2 py-1 font-mono text-[10px]" style={{ borderColor: "var(--c-accent)", color: "var(--c-accent)" }} data-testid={`riga-del-${r.id}`}>
                      <Icon.Trash className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
                  );
                });
              })()}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function SummaryTile({ label, current, delta, formatCurrent, formatDelta, suffix = "", note, highlight = false, testid }) {
  const hasDelta = delta != null && Number(delta) > 0;
  const total = Number(current) + Number(delta || 0);
  return (
    <div
      className={`min-w-[180px] px-3 py-2 border ${
        hasDelta ? "border-[var(--c-accent)] bg-white" : "border-[var(--c-border-soft)] bg-white"
      }`}
      data-testid={testid}
    >
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--c-text-muted)]">{label}</div>
      <div className={`font-display text-lg tabular-nums ${highlight ? "text-[var(--c-accent)]" : "text-[var(--c-text)]"}`}>
        {formatCurrent(total)}{suffix}
      </div>
      {hasDelta && (
        <div className="font-mono text-[10px] tabular-nums text-[var(--c-text-muted)]">
          <span>{formatCurrent(Number(current))}{suffix}</span>
          <span className="text-[var(--c-accent)] font-bold"> + {formatDelta(Number(delta))}{suffix}</span>
        </div>
      )}
      {!hasDelta && (
        <div className="font-mono text-[10px] tabular-nums text-[var(--c-text-muted)]">
          già in scheda
        </div>
      )}
      {note && (
        <div className="font-mono text-[9px] uppercase tracking-widest text-[var(--c-accent)] mt-0.5">
          {note}
        </div>
      )}
    </div>
  );
}


function DocPicker({ scheda, session, importedTables, onClose, onAdded }) {
  // We load Documenti via the existing /api/documenti endpoint and the list
  // of already-used doc_keys to grey out.
  const [docs, setDocs] = useState(null);
  const [used, setUsed] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(null); // {done, total} during bulk add
  const [selected, setSelected] = useState(() => new Set()); // multi-select
  // Per-doc date overrides. Defaults to the doc's own date when the user
  // ticks the row; can be edited inline before bulk-add.
  const [dateOverride, setDateOverride] = useState({}); // { doc_key: 'YYYY-MM-DD' }
  // Filters persist across picker opens (until user clicks "Pulisci")
  const FILTERS_KEY = "rimborsi.picker_filters.v1";
  const initialFilters = (() => {
    try {
      const raw = localStorage.getItem(FILTERS_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  })();
  const [origin, setOrigin] = useState(initialFilters.origin || "sede");
  const [setupAddr, setSetupAddr] = useState(null);
  const [filter, setFilter] = useState(initialFilters.filter || "");
  const [distanceFor, setDistanceFor] = useState(null); // doc row for map preview
  // Advanced filters
  const [dataDa, setDataDa] = useState(initialFilters.dataDa || "");
  const [dataA, setDataA] = useState(initialFilters.dataA || "");
  const [tipo, setTipo] = useState(initialFilters.tipo || "");
  const [error, setError] = useState("");
  // Persist filters whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(FILTERS_KEY, JSON.stringify({ origin, filter, dataDa, dataA, tipo }));
    } catch { /* quota — ignore */ }
  }, [origin, filter, dataDa, dataA, tipo]);
  // Reimbursement cache: docKey + origine -> km  (persisted in localStorage)
  const KM_CACHE_KEY = "rimborsi.km_cache.v2_ar";  // v2 = include andata+ritorno
  const [kmCache, setKmCache] = useState(() => {
    try {
      const raw = localStorage.getItem(KM_CACHE_KEY);
      return raw ? new Map(JSON.parse(raw)) : new Map();
    } catch { return new Map(); }
  });
  const persistCache = useCallback((cache) => {
    try { localStorage.setItem(KM_CACHE_KEY, JSON.stringify(Array.from(cache.entries()))); } catch (e) { /* quota */ }
  }, []);
  const costoKm = scheda?.automezzo?.costo_km || 0;
  const docTableExists = (importedTables || []).some((t) => t.name === "Documenti");

  useEffect(() => {
    // Need: documenti, sedi (for origin lat/lng), used doc_keys
    Promise.all([
      docTableExists
        ? axios.get(`${API}/access/${session?.session_id}/imported/Documenti?limit=5000`)
        : Promise.resolve({ data: { columns: [], rows: [] } }),
      axios.get(`${API}/setup`),
      axios.get(`${API}/rimborsi/documenti-usati`),
    ]).then(([d, s, u]) => {
      setDocs(d.data);
      setSetupAddr(s.data);
      setUsed(new Set(u.data.doc_keys || []));
    }).catch((e) => {
      setError(e?.response?.data?.detail || "Impossibile caricare i documenti");
    });
  }, [session, docTableExists]);

  // Normalize a doc row once
  const allDocs = useMemo(() => {
    if (!docs || !docs.rows) return [];
    const ci = {
      tipo: docs.columns.indexOf("Tipo"),
      data: docs.columns.indexOf("Data"),
      num: docs.columns.indexOf("Numero"),
      ana: docs.columns.indexOf("Anagrafica"),
      tot: docs.columns.indexOf("Totale"),
      indir: docs.columns.indexOf("_Indirizzo"),
      cap: docs.columns.indexOf("_CAP"),
      citta: docs.columns.indexOf("_Città"),
      prov: docs.columns.indexOf("_Provincia"),
    };
    const num = (v) => {
      if (v === null || v === undefined || v === "") return null;
      const n = Number(String(v).replace(",", "."));
      return Number.isFinite(n) ? n : null;
    };
    return docs.rows.map((r) => ({
      doc_key: `${r[ci.tipo]}|${r[ci.data]}|${r[ci.num]}|${r[ci.ana]}`,
      tipo: r[ci.tipo] || "",
      data: r[ci.data] || "",
      numero: r[ci.num],
      anagrafica: r[ci.ana] || "",
      totale: num(r[ci.tot]),
      indirizzo: r[ci.indir] || "",
      cap: r[ci.cap] || "",
      citta: r[ci.citta] || "",
      provincia: r[ci.prov] || "",
    }));
  }, [docs]);

  // Distinct tipi for the select
  const tipiAvailable = useMemo(() => {
    return Array.from(new Set(allDocs.map((d) => d.tipo).filter(Boolean))).sort();
  }, [allDocs]);

  const filterDocs = useMemo(() => {
    const want = (filter || "").trim().toLowerCase();
    const da = dataDa || "";
    const a = dataA || "";
    const t = tipo || "";
    return allDocs.filter((d) => {
      // Text filter
      if (want) {
        const hit = (
          (d.anagrafica || "").toLowerCase().includes(want) ||
          (d.citta || "").toLowerCase().includes(want) ||
          String(d.numero).includes(want)
        );
        if (!hit) return false;
      }
      // Date filter
      const dataIso = (d.data || "").slice(0, 10);
      if (da && dataIso < da) return false;
      if (a && dataIso > a) return false;
      // Tipo
      if (t && d.tipo !== t) return false;
      return true;
    });
  }, [allDocs, filter, dataDa, dataA, tipo]);

  const clearFilters = () => {
    setFilter(""); setDataDa(""); setDataA(""); setTipo("");
    setOrigin("sede");
    try { localStorage.removeItem(FILTERS_KEY); } catch { /* ignore */ }
  };

  // helper: geocode + directions for a single doc using mapbox
  const computeKm = useCallback(async (d) => {
    if (!MAPBOX_TOKEN) throw new Error("Mapbox non configurato");
    const originAddr = origin === "sede" ? setupAddr.sede : setupAddr.magazzino;
    if (!originAddr?.indirizzo) throw new Error(`${origin === "sede" ? "Sede" : "Magazzino"} non configurata`);
    const destParts = [d.indirizzo, d.cap, d.citta, d.provincia].filter(Boolean).join(", ");
    if (!destParts) throw new Error("Destinazione mancante nel documento");
    const oQ = [originAddr.indirizzo, originAddr.cap, originAddr.citta, originAddr.provincia].filter(Boolean).join(", ");
    const url = (q) => `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json?country=IT&limit=1&access_token=${MAPBOX_TOKEN}`;
    const [o, ds] = await Promise.all([axios.get(url(oQ)), axios.get(url(destParts))]);
    const oC = o.data.features?.[0]?.center;
    const dC = ds.data.features?.[0]?.center;
    if (!oC || !dC) throw new Error("Geocoding fallito");
    const dir = await axios.get(
      `https://api.mapbox.com/directions/v5/mapbox/driving/${oC[0]},${oC[1]};${dC[0]},${dC[1]}?access_token=${MAPBOX_TOKEN}&overview=false`,
    );
    const meters = dir.data.routes?.[0]?.distance;
    if (typeof meters !== "number") throw new Error("Calcolo percorso fallito");
    // La distanza salvata è la singola tratta. Il rimborso viene calcolato
    // come 2 × km × costo/km nella riga della scheda (andata + ritorno).
    return Math.round((meters / 1000) * 100) / 100;
  }, [origin, setupAddr]);

  const cacheKey = (docKey, ori) => `${ori}|${docKey}`;
  const lookupKm = useCallback((d) => {
    const k = cacheKey(d.doc_key, origin);
    return kmCache.has(k) ? kmCache.get(k) : null;
  }, [kmCache, origin]);

  // Toggle a doc into the multi-select set. If the doc has no cached km
  // yet, kick off a background calculation so the totals tile updates
  // automatically as soon as the value is known.
  const toggleSelected = (d) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(d.doc_key)) {
        next.delete(d.doc_key);
      } else {
        next.add(d.doc_key);
        const existing = kmCache.get(cacheKey(d.doc_key, origin));
        if (existing == null) {
          // Fire-and-forget so the click stays snappy.
          (async () => {
            try {
              const km = await computeKm(d);
              setKmCache((cur) => {
                const out = new Map(cur);
                out.set(cacheKey(d.doc_key, origin), km);
                persistCache(out);
                return out;
              });
            } catch {
              // If geocode/route fails, mark as -1 so the UI shows "no percorso"
              setKmCache((cur) => {
                const out = new Map(cur);
                out.set(cacheKey(d.doc_key, origin), -1);
                persistCache(out);
                return out;
              });
            }
          })();
        }
      }
      return next;
    });
  };

  // Selected docs that are still valid (have address, not used). Memoized so
  // the totals badge updates in real time as the user picks/unpicks.
  const selectedDocs = useMemo(() => {
    if (selected.size === 0) return [];
    const map = new Map(allDocs.map((d) => [d.doc_key, d]));
    const out = [];
    for (const k of selected) {
      const d = map.get(k);
      if (!d) continue;
      if (used.has(d.doc_key)) continue;
      if (!(d.indirizzo || d.citta)) continue;
      out.push(d);
    }
    return out;
  }, [selected, allDocs, used]);

  const selectedTotals = useMemo(() => {
    let kmKnown = 0;
    let kmCount = 0;
    let kmMissing = 0;
    for (const d of selectedDocs) {
      const k = kmCache.get(cacheKey(d.doc_key, origin));
      if (k != null && k >= 0) {
        kmKnown += k;
        kmCount += 1;
      } else {
        kmMissing += 1;
      }
    }
    return {
      righe: selectedDocs.length,
      km_known: kmKnown,
      km_count: kmCount,
      km_missing: kmMissing,
      euro: 2 * kmKnown * costoKm,
    };
  }, [selectedDocs, kmCache, origin, costoKm]);

  // Bulk add: process selected docs sequentially. We use the singular endpoint
  // /righe per doc (it's idempotent thanks to doc_key uniqueness server-side)
  // and refresh totals progressively so the UI stays responsive.
  const addSelected = async () => {
    if (selectedDocs.length === 0) return;
    if (selectedTotals.km_missing > 0) {
      const ok = window.confirm(
        `${selectedTotals.km_missing} document${selectedTotals.km_missing === 1 ? "o" : "i"} non hanno ancora km calcolato. Verranno calcolati ora (può richiedere qualche secondo). Continuare?`
      );
      if (!ok) return;
    }
    setBulkBusy({ done: 0, total: selectedDocs.length });
    let lastScheda = null;
    const nextCache = new Map(kmCache);
    const errors = [];
    for (let i = 0; i < selectedDocs.length; i++) {
      const d = selectedDocs[i];
      try {
        const cached = nextCache.get(cacheKey(d.doc_key, origin));
        const km = (cached != null && cached >= 0) ? cached : await computeKm(d);
        if (cached == null) {
          nextCache.set(cacheKey(d.doc_key, origin), km);
        }
        const r = await axios.post(`${API}/rimborsi/schede/${scheda.id}/righe`, {
          doc_key: d.doc_key,
          data: (dateOverride[d.doc_key] || String(d.data || "").slice(0, 10)),
          data_doc: String(d.data || "").slice(0, 10),
          anagrafica: d.anagrafica,
          indirizzo: d.indirizzo,
          cap: d.cap,
          citta: d.citta,
          provincia: d.provincia,
          origine: origin,
          km: Math.round(km * 100) / 100,
        });
        lastScheda = r.data;
        // Update the used set so the row turns grey/disabled immediately
        setUsed((prev) => new Set(prev).add(d.doc_key));
      } catch (e) {
        const msg = e?.response?.data?.detail || e.message || "errore";
        errors.push(`${d.anagrafica}: ${msg}`);
      }
      setBulkBusy({ done: i + 1, total: selectedDocs.length });
    }
    setKmCache(nextCache);
    persistCache(nextCache);
    setBulkBusy(null);
    setSelected(new Set());
    if (lastScheda) onAdded(lastScheda);
    if (errors.length === 0) {
      toast.success(`${selectedDocs.length} righe aggiunte.`);
    } else {
      toast.error(`${selectedDocs.length - errors.length} OK, ${errors.length} fallite. Vedi console.`);
      // eslint-disable-next-line no-console
      console.warn("Bulk add errors:", errors);
    }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white border-2 border-[var(--c-text)] w-full max-w-5xl max-h-[90vh] flex flex-col" data-testid="doc-picker">
        <div className="p-4 border-b border-[var(--c-border-soft)] flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--c-text-muted)]">Aggiungi righe</div>
            <div className="font-display text-xl">Seleziona Documenti</div>
          </div>
          <button onClick={onClose} className="btn-ghost px-3 py-2 font-mono text-[10px] uppercase tracking-widest" data-testid="picker-close">Chiudi</button>
        </div>

        {/* Summary bar — always visible, shows running totals incl. selection */}
        <div className="px-4 py-3 bg-[var(--c-surface)] border-b border-[var(--c-border-soft)] flex items-stretch gap-2 flex-wrap" data-testid="picker-totals">
          <SummaryTile
            label="Righe"
            current={scheda.righe_count || 0}
            delta={selectedTotals.righe}
            formatCurrent={(n) => n}
            formatDelta={(n) => n}
            testid="sum-righe"
          />
          <SummaryTile
            label="Totale Km"
            current={Number(scheda.totale_km) || 0}
            delta={selectedTotals.km_known}
            formatCurrent={(n) => formatItalianNumber(n, 2)}
            formatDelta={(n) => formatItalianNumber(n, 2)}
            suffix=" km"
            note={selectedTotals.km_missing > 0
              ? `+ ${selectedTotals.km_missing} senza km calcolato`
              : null}
            testid="sum-km"
          />
          <SummaryTile
            label="Totale €"
            current={Number(scheda.totale_euro) || 0}
            delta={selectedTotals.euro}
            formatCurrent={(n) => `€ ${formatItalianNumber(n, 2)}`}
            formatDelta={(n) => `€ ${formatItalianNumber(n, 2)}`}
            highlight
            testid="sum-euro"
          />
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSelected(new Set())}
              disabled={selected.size === 0 || bulkBusy != null}
              className="btn-ghost px-3 py-2 font-mono text-[10px] uppercase tracking-widest disabled:opacity-30"
              data-testid="bulk-clear-selection"
            >
              Pulisci selezione
            </button>
            <button
              type="button"
              onClick={addSelected}
              disabled={selectedDocs.length === 0 || bulkBusy != null}
              className="px-4 py-2 font-mono text-xs uppercase tracking-widest bg-[var(--c-accent)] text-white hover:opacity-90 disabled:opacity-40"
              data-testid="bulk-add"
            >
              {bulkBusy
                ? `Aggiungo ${bulkBusy.done}/${bulkBusy.total}…`
                : `Aggiungi ${selectedDocs.length || ""} selezionati`}
            </button>
          </div>
        </div>

        <div className="p-4 border-b border-[var(--c-border-soft)] grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 items-end">
          <div className="col-span-2 sm:col-span-1">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--c-text-muted)] mb-1.5">Origine</div>
            <div className="flex gap-1">
              <button onClick={() => setOrigin("sede")} className={`flex-1 px-3 py-2 font-mono text-[10px] uppercase tracking-widest border ${origin === "sede" ? "bg-[var(--c-accent)] text-white border-[var(--c-accent)]" : "border-[var(--c-text)]"}`} data-testid="origin-sede">Sede</button>
              <button onClick={() => setOrigin("magazzino")} className={`flex-1 px-3 py-2 font-mono text-[10px] uppercase tracking-widest border ${origin === "magazzino" ? "bg-[var(--c-accent)] text-white border-[var(--c-accent)]" : "border-[var(--c-text)]"}`} data-testid="origin-mag">Magazzino</button>
            </div>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--c-text-muted)] mb-1.5">Data da</div>
            <input type="date" value={dataDa} onChange={(e) => setDataDa(e.target.value)} className="w-full px-2 py-2 bg-white border border-[var(--c-text)] font-mono text-xs" data-testid="filter-data-da" />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--c-text-muted)] mb-1.5">Data a</div>
            <input type="date" value={dataA} onChange={(e) => setDataA(e.target.value)} className="w-full px-2 py-2 bg-white border border-[var(--c-text)] font-mono text-xs" data-testid="filter-data-a" />
          </div>
          <div className="flex items-end gap-2 col-span-2">
            <div className="flex-1 min-w-[180px]">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--c-text-muted)] mb-1.5">Tipo doc.</div>
              <select value={tipo} onChange={(e) => setTipo(e.target.value)} className="w-full px-2 py-2 bg-white border border-[var(--c-text)] font-mono text-xs" data-testid="filter-tipo">
                <option value="">— tutti —</option>
                {tipiAvailable.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <button
              onClick={clearFilters}
              className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest border border-[var(--c-accent)] bg-[var(--c-accent)] text-white hover:bg-[var(--c-accent-hover)] transition-colors whitespace-nowrap"
              data-testid="filter-clear"
            >
              Pulisci
            </button>
          </div>
          <div className="col-span-2 sm:col-span-4 lg:col-span-6 flex items-center gap-3 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--c-text-muted)] mb-1.5">Cerca testuale</div>
              <input type="text" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Cliente / Città / Numero" className="w-full px-3 py-2 bg-white border border-[var(--c-text)] font-mono text-sm" data-testid="picker-filter" />
            </div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--c-text-muted)] mt-5">
              <span className="text-[var(--c-text)] font-bold tabular-nums">{filterDocs.length}</span> / {allDocs.length}
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-auto p-4">
          {error ? (
            <div className="font-mono text-xs" style={{ color: "var(--c-error)" }}>{error}</div>
          ) : !docs ? (
            <div className="font-mono text-xs text-[var(--c-text-muted)]">Caricamento documenti…</div>
          ) : !docTableExists ? (
            <div className="font-mono text-xs text-[var(--c-text-muted)]">Nessun archivio Documenti importato. Importa prima un file Access.</div>
          ) : filterDocs.length === 0 ? (
            <div className="font-mono text-xs text-[var(--c-text-muted)]">Nessun documento corrisponde al filtro.</div>
          ) : (
            <div className="space-y-1">
              {filterDocs.map((d) => {
                const isUsed = used.has(d.doc_key);
                const isSel = selected.has(d.doc_key);
                const hasAddr = (d.indirizzo || d.citta) && true;
                const cachedKm = lookupKm(d);
                const rimborso = (cachedKm != null && cachedKm >= 0) ? cachedKm * costoKm : null;
                return (
                  <div
                    key={d.doc_key}
                    className={`flex items-stretch border font-mono text-xs ${
                      isUsed ? "border-[var(--c-border-soft)] bg-[var(--c-surface)] opacity-50"
                        : !hasAddr ? "border-[var(--c-border-soft)] opacity-40"
                        : isSel ? "border-[var(--c-accent)] bg-[#fff5f5]"
                        : "border-[var(--c-text)]"
                    }`}
                  >
                    <button
                      onClick={() => toggleSelected(d)}
                      disabled={isUsed || !hasAddr || bulkBusy != null}
                      className={`flex-1 text-left px-3 py-2 flex items-center gap-3 ${
                        isUsed || !hasAddr ? "cursor-not-allowed" : "cursor-pointer hover:bg-[var(--c-surface)]"
                      }`}
                      data-testid={`pick-${d.doc_key}`}
                    >
                      <input
                        type="checkbox"
                        checked={isSel}
                        readOnly
                        disabled={isUsed || !hasAddr || bulkBusy != null}
                        className="pointer-events-none"
                        data-testid={`check-${d.doc_key}`}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="font-bold">{d.anagrafica || "—"}</div>
                        <div className="text-[var(--c-text-muted)] truncate">{[d.indirizzo, d.cap, d.citta, d.provincia && `(${d.provincia})`].filter(Boolean).join(" ") || "Nessun indirizzo"}</div>
                      </div>
                      <div
                        className="flex flex-col items-end gap-1 text-right whitespace-nowrap"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex flex-col items-end gap-0.5">
                          <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--c-text-muted)]">
                            Data doc.
                          </span>
                          <span className="font-mono text-[11px]" data-testid={`row-data-doc-${d.doc_key}`}>
                            {formatItalianDate(d.data)} · #{d.numero}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--c-text-muted)]">
                            Data viaggio
                          </span>
                          <input
                            type="date"
                            value={dateOverride[d.doc_key] != null
                              ? dateOverride[d.doc_key]
                              : (String(d.data || "").slice(0, 10))}
                            onChange={(e) => setDateOverride((p) => ({ ...p, [d.doc_key]: e.target.value }))}
                            onClick={(e) => e.stopPropagation()}
                            disabled={isUsed || !hasAddr || bulkBusy != null}
                            className="px-1.5 py-0.5 border border-[var(--c-accent)] font-mono text-[11px] disabled:opacity-50"
                            data-testid={`row-date-${d.doc_key}`}
                          />
                        </div>
                        {d.tipo && (
                          <div className="text-[10px] uppercase text-[var(--c-text-muted)] tracking-widest">{d.tipo}</div>
                        )}
                        {rimborso != null ? (
                          <div className="tabular-nums font-bold text-[var(--c-accent)]">
                            € {formatItalianNumber(rimborso, 2)}
                            <span className="text-[10px] text-[var(--c-text-muted)] font-normal ml-1">({formatItalianNumber(cachedKm, 1)} km)</span>
                          </div>
                        ) : cachedKm === -1 ? (
                          <div className="text-[10px] text-[var(--c-text-muted)] uppercase">no percorso</div>
                        ) : null}
                        <div className="text-[var(--c-text-muted)] text-[10px] uppercase">
                          {isUsed ? "Già in scheda" : !hasAddr ? "No indir." : isSel ? "Selezionato ✓" : "Seleziona"}
                        </div>
                      </div>
                    </button>
                    {hasAddr && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          // Synthesize the row shape DistanceModal expects.
                          const columns = ["Anagrafica", "_Indirizzo", "_CAP", "_Città", "_Provincia"];
                          const row = [d.anagrafica, d.indirizzo, d.cap, d.citta, d.provincia];
                          setDistanceFor({ row, columns });
                        }}
                        disabled={isUsed}
                        className="px-3 border-l border-[var(--c-border-soft)] hover:bg-[var(--c-surface)] disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
                        title="Calcola distanza e mostra mappa (non aggiunge alla scheda)"
                        data-testid={`picker-map-${d.doc_key}`}
                      >
                        <Icon.MapPin className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {distanceFor && (
        <DistanceModal
          rowData={distanceFor}
          onClose={() => setDistanceFor(null)}
        />
      )}
    </div>
  );
}

export default App;

/* ----------------------------------------------------------------------- */
/* Soggetti: CRUD anagrafica persone fisiche                                 */
/* ----------------------------------------------------------------------- */

const EMPTY_SOGGETTO = { cognome: "", nome: "", nato_a: "", nato_il: "" };

function SoggettiView() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/soggetti`);
      setItems(r.data);
    } catch (e) {
      toast.error("Impossibile caricare i soggetti.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startNew = () => setEditing({ ...EMPTY_SOGGETTO });
  const startEdit = (item) =>
    setEditing({
      id: item.id,
      cognome: item.cognome,
      nome: item.nome,
      nato_a: item.nato_a || "",
      nato_il: item.nato_il || "",
    });
  const cancel = () => setEditing(null);

  const save = async () => {
    if (!editing) return;
    if (!editing.cognome.trim() || !editing.nome.trim()) {
      toast.error("Cognome e Nome sono obbligatori.");
      return;
    }
    setBusy(true);
    try {
      const body = {
        cognome: editing.cognome.trim(),
        nome: editing.nome.trim(),
        nato_a: editing.nato_a.trim(),
        nato_il: editing.nato_il.trim(),
      };
      if (editing.id) {
        await axios.put(`${API}/soggetti/${editing.id}`, body);
        toast.success("Soggetto aggiornato.");
      } else {
        await axios.post(`${API}/soggetti`, body);
        toast.success("Soggetto creato.");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Salvataggio fallito.");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Eliminare ${item.cognome} ${item.nome}?`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/soggetti/${item.id}`);
      toast.success("Soggetto eliminato.");
      load();
    } catch (e) {
      toast.error("Eliminazione fallita.");
    } finally {
      setBusy(false);
    }
  };

  const formatBirthDate = (iso) => {
    if (!iso) return "";
    const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
  };

  return (
    <div className="flex-1 overflow-auto p-8" data-testid="soggetti-view">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--c-text-muted)]">
              Configurazione
            </div>
            <h2 className="font-display text-3xl">Soggetti</h2>
            <p className="font-mono text-xs text-[var(--c-text-muted)] mt-1">
              Anagrafica persone fisiche · cognome, nome, dati di nascita
            </p>
          </div>
          <button
            type="button"
            onClick={startNew}
            disabled={editing != null || busy}
            className="px-4 py-3 font-mono text-xs uppercase tracking-widest bg-[var(--c-accent)] text-white hover:opacity-90 disabled:opacity-40 transition-opacity flex items-center gap-2"
            data-testid="soggetto-new"
          >
            <Icon.Plus className="w-4 h-4" /> Nuovo
          </button>
        </div>

        {editing && (
          <div className="bg-[var(--c-surface)] border border-[var(--c-border-soft)] p-5 mb-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--c-text-muted)] mb-3">
              {editing.id ? "Modifica soggetto" : "Nuovo soggetto"}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <SetupField
                label="Cognome"
                value={editing.cognome}
                onChange={(v) => setEditing((p) => ({ ...p, cognome: v }))}
                testid="form-cognome"
                maxLength={60}
              />
              <SetupField
                label="Nome"
                value={editing.nome}
                onChange={(v) => setEditing((p) => ({ ...p, nome: v }))}
                testid="form-nome"
                maxLength={60}
              />
              <SetupField
                label="Nato a"
                value={editing.nato_a}
                onChange={(v) => setEditing((p) => ({ ...p, nato_a: v }))}
                placeholder="Milano"
                testid="form-nato-a"
                maxLength={80}
              />
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--c-text-muted)] mb-1.5">
                  Nato il
                </div>
                <input
                  type="date"
                  value={editing.nato_il}
                  onChange={(e) => setEditing((p) => ({ ...p, nato_il: e.target.value }))}
                  className="w-full px-3 py-2.5 bg-white border border-[var(--c-text)] font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[var(--c-accent)] focus:ring-offset-1"
                  data-testid="form-nato-il"
                />
              </div>
              <div className="sm:col-span-2 flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={cancel}
                  disabled={busy}
                  className="btn-ghost px-4 py-3 font-mono text-xs uppercase tracking-widest"
                  data-testid="form-cancel"
                >
                  Annulla
                </button>
                <button
                  type="button"
                  onClick={save}
                  disabled={busy}
                  className="px-4 py-3 font-mono text-xs uppercase tracking-widest bg-[var(--c-accent)] text-white hover:opacity-90 disabled:opacity-40 transition-opacity"
                  data-testid="form-save"
                >
                  Salva
                </button>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="font-mono text-xs text-[var(--c-text-muted)]">Caricamento…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 border border-dashed border-[var(--c-border-soft)] font-mono text-sm text-[var(--c-text-muted)]">
            Nessun soggetto. Clicca <strong className="text-[var(--c-accent)]">NUOVO</strong> per inserirne uno.
          </div>
        ) : (
          <table className="w-full font-mono text-sm">
            <thead>
              <tr>
                <th className="text-left px-3 py-2 border-b border-[var(--c-text)] bg-[var(--c-surface)] text-[10px] uppercase tracking-widest font-bold">Cognome</th>
                <th className="text-left px-3 py-2 border-b border-[var(--c-text)] bg-[var(--c-surface)] text-[10px] uppercase tracking-widest font-bold">Nome</th>
                <th className="text-left px-3 py-2 border-b border-[var(--c-text)] bg-[var(--c-surface)] text-[10px] uppercase tracking-widest font-bold">Nato a</th>
                <th className="text-left px-3 py-2 border-b border-[var(--c-text)] bg-[var(--c-surface)] text-[10px] uppercase tracking-widest font-bold">Nato il</th>
                <th className="text-right px-3 py-2 border-b border-[var(--c-text)] bg-[var(--c-surface)] text-[10px] uppercase tracking-widest font-bold">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr
                  key={it.id}
                  className="hover:bg-[var(--c-surface)] transition-colors"
                  data-testid={`soggetto-row-${it.id}`}
                >
                  <td className="px-3 py-2 border-b border-[var(--c-border-soft)]">{it.cognome}</td>
                  <td className="px-3 py-2 border-b border-[var(--c-border-soft)]">{it.nome}</td>
                  <td className="px-3 py-2 border-b border-[var(--c-border-soft)]">{it.nato_a}</td>
                  <td className="px-3 py-2 border-b border-[var(--c-border-soft)] tabular-nums">{formatBirthDate(it.nato_il)}</td>
                  <td className="px-3 py-2 border-b border-[var(--c-border-soft)] text-right">
                    <div className="inline-flex gap-2">
                      <button
                        type="button"
                        onClick={() => startEdit(it)}
                        disabled={busy || editing != null}
                        className="btn-ghost px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest disabled:opacity-30"
                        data-testid={`soggetto-edit-${it.id}`}
                      >
                        Modifica
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(it)}
                        disabled={busy || editing != null}
                        className="btn-ghost px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest disabled:opacity-30"
                        style={{ borderColor: "var(--c-accent)", color: "var(--c-accent)" }}
                        data-testid={`soggetto-del-${it.id}`}
                      >
                        <Icon.Trash className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/* Distance Modal: shows route map and distance between sede/magazzino       */
/* and the document's client address.                                        */
/* ----------------------------------------------------------------------- */

function DistanceModal({ rowData, onClose }) {
  const [origin, setOrigin] = useState("sede"); // 'sede' | 'magazzino'
  const [setup, setSetup] = useState(null);
  const [route, setRoute] = useState(null); // {distance_m, duration_s, geometry}
  const [originCoords, setOriginCoords] = useState(null); // [lng, lat]
  const [destCoords, setDestCoords] = useState(null);
  const [step, setStep] = useState("init"); // init | loading | ready | error
  const [errMsg, setErrMsg] = useState("");
  const [automezzi, setAutomezzi] = useState([]);
  const [automezzoId, setAutomezzoId] = useState(
    () => localStorage.getItem("distance_modal_automezzo_id") || ""
  );
  const mapEl = useRef(null);
  const mapInstance = useRef(null);

  // Extract destination from row using the hidden columns we added
  const dest = useMemo(() => {
    const cols = rowData.columns;
    const r = rowData.row;
    const idx = (name) => cols.indexOf(name);
    const get = (name) => {
      const i = idx(name);
      return i >= 0 && r[i] ? String(r[i]).trim() : "";
    };
    return {
      anagrafica: get("Anagrafica"),
      indirizzo: get("_Indirizzo"),
      cap: get("_CAP"),
      citta: get("_Città"),
      provincia: get("_Provincia"),
    };
  }, [rowData]);

  const destQuery = useMemo(() => {
    const parts = [
      dest.indirizzo,
      [dest.cap, dest.citta].filter(Boolean).join(" "),
      dest.provincia,
      "Italia",
    ].filter(Boolean);
    return parts.join(", ");
  }, [dest]);

  // Load setup once
  useEffect(() => {
    axios
      .get(`${API}/setup`)
      .then((r) => setSetup(r.data))
      .catch(() => setSetup(null));
  }, []);

  // Load automezzi once (for cost calculation)
  useEffect(() => {
    axios
      .get(`${API}/automezzi`)
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : [];
        setAutomezzi(list);
        // If saved selection is invalid, clear it
        setAutomezzoId((prev) => {
          if (prev && list.some((a) => a.id === prev)) return prev;
          return list.length === 1 ? list[0].id : "";
        });
      })
      .catch(() => setAutomezzi([]));
  }, []);

  // Persist vehicle selection
  useEffect(() => {
    if (automezzoId) localStorage.setItem("distance_modal_automezzo_id", automezzoId);
  }, [automezzoId]);

  const originAddr = useMemo(() => {
    if (!setup) return null;
    return setup[origin] || null;
  }, [setup, origin]);

  const originQuery = useMemo(() => {
    if (!originAddr) return "";
    const parts = [
      originAddr.indirizzo,
      [originAddr.cap, originAddr.citta].filter(Boolean).join(" "),
      originAddr.provincia,
      "Italia",
    ].filter(Boolean);
    return parts.join(", ");
  }, [originAddr]);

  const calculate = useCallback(async () => {
    if (!MAPBOX_TOKEN) {
      setStep("error");
      setErrMsg("Mapbox token mancante.");
      return;
    }
    if (!originQuery) {
      setStep("error");
      setErrMsg(`Indirizzo ${origin === "sede" ? "sede" : "magazzino"} non configurato. Vai su Setup.`);
      return;
    }
    if (!dest.indirizzo && !dest.citta) {
      setStep("error");
      setErrMsg("Indirizzo cliente non disponibile.");
      return;
    }
    setStep("loading");
    setErrMsg("");
    try {
      const geocode = async (q) => {
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json?country=IT&limit=1&access_token=${MAPBOX_TOKEN}`;
        const r = await axios.get(url);
        if (!r.data.features || r.data.features.length === 0) {
          throw new Error(`Indirizzo non trovato: ${q}`);
        }
        return r.data.features[0].center; // [lng, lat]
      };
      const [oc, dc] = await Promise.all([
        geocode(originQuery),
        geocode(destQuery),
      ]);
      setOriginCoords(oc);
      setDestCoords(dc);
      const routeUrl =
        `https://api.mapbox.com/directions/v5/mapbox/driving/` +
        `${oc[0]},${oc[1]};${dc[0]},${dc[1]}?` +
        `geometries=geojson&overview=full&access_token=${MAPBOX_TOKEN}`;
      const rr = await axios.get(routeUrl);
      if (!rr.data.routes || rr.data.routes.length === 0) {
        throw new Error("Percorso non trovato.");
      }
      const route0 = rr.data.routes[0];
      setRoute({
        distance_m: route0.distance,
        duration_s: route0.duration,
        geometry: route0.geometry,
      });
      setStep("ready");
    } catch (e) {
      setStep("error");
      setErrMsg(e?.message || "Errore nel calcolo del percorso.");
    }
  }, [origin, originQuery, dest, destQuery]);

  // Auto-calculate when origin/setup become ready (or when user switches origin)
  useEffect(() => {
    if (setup && originQuery && destQuery) calculate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setup, origin]);

  // Render map when route is ready
  useEffect(() => {
    if (step !== "ready" || !mapEl.current || !route) return;
    mapboxgl.accessToken = MAPBOX_TOKEN;
    if (mapInstance.current) {
      mapInstance.current.remove();
      mapInstance.current = null;
    }
    const map = new mapboxgl.Map({
      container: mapEl.current,
      style: "mapbox://styles/mapbox/streets-v12",
      bounds: [
        [Math.min(originCoords[0], destCoords[0]), Math.min(originCoords[1], destCoords[1])],
        [Math.max(originCoords[0], destCoords[0]), Math.max(originCoords[1], destCoords[1])],
      ],
      fitBoundsOptions: { padding: 60 },
    });
    map.addControl(new mapboxgl.NavigationControl(), "top-right");
    map.on("load", () => {
      // Force resize after mount — the dialog can finish its open animation
      // after mapbox-gl has already captured initial container dimensions,
      // which leaves the canvas at 0×0 otherwise.
      map.resize();
      map.addSource("route", { type: "geojson", data: { type: "Feature", properties: {}, geometry: route.geometry } });
      map.addLayer({
        id: "route-line",
        type: "line",
        source: "route",
        layout: { "line-join": "round", "line-cap": "round" },
        paint: { "line-color": "#FF3B30", "line-width": 5 },
      });
      new mapboxgl.Marker({ color: "#0A0A0A" }).setLngLat(originCoords).setPopup(new mapboxgl.Popup().setText(origin === "sede" ? "Sede" : "Magazzino")).addTo(map);
      new mapboxgl.Marker({ color: "#FF3B30" }).setLngLat(destCoords).setPopup(new mapboxgl.Popup().setText(dest.anagrafica || "Cliente")).addTo(map);
    });
    // Belt-and-braces: also resize on a couple of next frames in case the
    // container height changes due to layout settling.
    const r1 = requestAnimationFrame(() => map.resize());
    const r2 = setTimeout(() => map.resize(), 200);

    // Observe container size and resize the map whenever it changes —
    // covers viewport rotation, sidebar collapses, dialog transitions, etc.
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => map.resize());
      ro.observe(mapEl.current);
    }
    mapInstance.current = map;
    return () => {
      cancelAnimationFrame(r1);
      clearTimeout(r2);
      if (ro) ro.disconnect();
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }
    };
  }, [step, route, origin, originCoords, destCoords, dest]);

  // Cleanup on close
  useEffect(() => () => {
    if (mapInstance.current) {
      mapInstance.current.remove();
      mapInstance.current = null;
    }
  }, []);

  const km = route ? (route.distance_m / 1000) : null;
  const durationMin = route ? Math.round(route.duration_s / 60) : null;
  const selectedAutomezzo = useMemo(
    () => automezzi.find((a) => a.id === automezzoId) || null,
    [automezzi, automezzoId]
  );
  const costoKm = selectedAutomezzo ? Number(selectedAutomezzo.costo_km) || 0 : 0;
  const costo = km != null && selectedAutomezzo ? km * costoKm : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(10,10,10,0.75)" }}
      data-testid="distance-modal"
      onClick={onClose}
    >
      <div
        className="bg-white border border-[var(--c-text)] w-full max-w-5xl h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-6 py-4 border-b border-[var(--c-text)] flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--c-text-muted)]">
              Distanza e percorso
            </div>
            <h3 className="font-display text-2xl truncate" data-testid="modal-title">
              {dest.anagrafica || "Cliente"}
            </h3>
            <div className="font-mono text-xs text-[var(--c-text-muted)] truncate" title={destQuery}>
              {destQuery || "(indirizzo cliente mancante)"}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="btn-ghost px-3 py-2 font-mono text-xs uppercase tracking-widest flex items-center gap-1"
            data-testid="modal-close"
          >
            <Icon.X className="w-4 h-4" />
            Chiudi
          </button>
        </header>

        <div className="px-6 py-4 border-b border-[var(--c-border-soft)] flex flex-col sm:flex-row sm:items-center gap-4 flex-wrap">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setOrigin("sede")}
              className={`px-4 py-2 font-mono text-xs uppercase tracking-widest border ${
                origin === "sede"
                  ? "bg-[var(--c-text)] text-white border-[var(--c-text)]"
                  : "border-[var(--c-text)] hover:bg-[var(--c-surface)]"
              }`}
              data-testid="origin-sede"
            >
              Sede
            </button>
            <button
              type="button"
              onClick={() => setOrigin("magazzino")}
              className={`px-4 py-2 font-mono text-xs uppercase tracking-widest border ${
                origin === "magazzino"
                  ? "bg-[var(--c-text)] text-white border-[var(--c-text)]"
                  : "border-[var(--c-text)] hover:bg-[var(--c-surface)]"
              }`}
              data-testid="origin-magazzino"
            >
              Magazzino
            </button>
          </div>
          {originAddr && (
            <div className="font-mono text-[11px] text-[var(--c-text-muted)] truncate max-w-[280px]" title={originQuery}>
              da: {originQuery}
            </div>
          )}
          <div className="flex items-center gap-2">
            <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-[var(--c-text-muted)]">
              Mezzo
            </span>
            <select
              value={automezzoId}
              onChange={(e) => setAutomezzoId(e.target.value)}
              className="input-base font-mono text-xs px-2 py-1 min-w-[180px]"
              data-testid="distance-modal-vehicle-select"
            >
              <option value="">— Nessuno —</option>
              {automezzi.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.automezzo} ({a.targa}) · €{formatItalianNumber(a.costo_km, 3)}/km
                </option>
              ))}
            </select>
          </div>
          <div className="sm:ml-auto flex gap-6 font-mono">
            <div data-testid="modal-distance">
              <div className="text-[9px] uppercase tracking-[0.18em] text-[var(--c-text-muted)]">Distanza (tratta)</div>
              <div className="text-lg font-bold">
                {km != null ? `${formatItalianNumber(km, 1)} km` : "—"}
              </div>
            </div>
            <div data-testid="modal-duration">
              <div className="text-[9px] uppercase tracking-[0.18em] text-[var(--c-text-muted)]">Tempo (tratta)</div>
              <div className="text-lg font-bold">
                {durationMin != null ? `${durationMin} min` : "—"}
              </div>
            </div>
            <div data-testid="modal-cost">
              <div className="text-[9px] uppercase tracking-[0.18em] text-[var(--c-text-muted)]">Rimborso A/R</div>
              <div className="text-lg font-bold" title={selectedAutomezzo ? `2 × ${formatItalianNumber(km || 0, 2)} km × €${formatItalianNumber(costoKm, 3)}/km` : "Seleziona un mezzo"}>
                {costo != null ? `€ ${formatItalianNumber(costo * 2, 2)}` : "—"}
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 relative" style={{ minHeight: 420 }}>
          {step === "loading" && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-10 pointer-events-none">
              <div>
                <div className="scanline-bar w-48" />
                <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--c-text-muted)] mt-2 text-center">
                  Calcolo percorso…
                </div>
              </div>
            </div>
          )}
          {step === "error" && (
            <div className="absolute inset-0 flex items-center justify-center p-8 text-center z-10 bg-white">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--c-error)" }}>
                  Errore
                </div>
                <div className="font-mono text-sm mt-2 max-w-md">{errMsg}</div>
              </div>
            </div>
          )}
          <div
            ref={mapEl}
            data-testid="map-canvas"
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
          />
        </div>
      </div>
    </div>
  );
}

function FilterField({ label, children }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-[var(--c-text-muted)]">
        {label}
      </span>
      {children}
    </label>
  );
}
